import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ShoppingCart, Package, FileText, Truck, CheckCircle2, AlertTriangle,
  Plus, Search, X, Loader2, Building2, ClipboardList, Receipt,
  ArrowRight, BadgeCheck, Clock, DollarSign, TrendingUp, Banknote,
  ChevronDown, ChevronRight, Eye, BarChart2, MapPin, Filter,
  Check, RotateCcw, Calendar, AlertCircle, Boxes, MessageSquare,
  Download, Printer, History, FileCheck, Pencil, Paperclip, UploadCloud,
  List, BookOpen, Tag, ExternalLink, Brain, Sparkles, FileUp, CheckCheck, Zap, Trash2, Wallet
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { Modal, StatCard, StatusBadge, StatusTimeline, PedidoDetailModal, SelectAPPdfOptionsModal, HistorialPedidoCard } from "./SharedComponents";
import { FLOW_STEPS, STATUS_COLORS, STATUS_LABELS, UNITS, WORK_FRONTS, URGENCY_LABELS } from "./constants";
import { formatDateTime, buildHistory, generatePedidoPDF, generatePPPDF, generateAPPDF } from "./helpers";
import { FiltroClienteObraPresupuesto, matchClienteObraPresupuesto } from "./FiltroClienteObraPresupuesto";
import { BusquedaEstadoUrgenciaFilters, sortPendingFirst } from "./BusquedaFilters";
import { roleAllowed } from "../../utils/roles";
import { AsignarStockAPModal } from "./AsignarStockAPModal";
import { CompraCajaChicaAPModal } from "./CompraCajaChicaAPModal";   // Fix GC
import { AprobacionAP, ChipAprobacionAP, AprobacionProveedorAP, proveedoresAbiertosAP } from "./RangosAprobacionTab";   // Fix EX · Fix HI

// BLOQUE 5 PARTE A — cinturón extra contra el off-by-one de la IA: si el item_index
// devuelto no matchea el nombre del ítem destino, buscamos el índice correcto por nombre.
const _normName = (s) => (s || "").toString().trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const _namesMatch = (a, b) => {
  const na = _normName(a), nb = _normName(b);
  if (!na || !nb) return false;
  return na === nb || na.includes(nb) || nb.includes(na);
};
const _resolveAiTargetIdx = (extracted, items) => {
  const idx = extracted.item_index;
  if (typeof idx !== "number" || idx < 0 || idx >= items.length) return -1;
  if (_namesMatch(extracted.item_name, items[idx]?.item_name)) return idx;
  const found = items.findIndex(it => _namesMatch(extracted.item_name, it.item_name));
  return found >= 0 ? found : idx;
};

// CEN 2.0 FASE 3 PARTE C — semáforo de precios: compara el P.U. cotizado contra el
// costo de referencia del presupuesto (ref_precio_mat, snapshoteado con IVA incluido
// al crear el AP). Verde por debajo, amarillo ±1%, rojo por encima. Informativo, no
// bloqueante — la cantidad y el precio siguen 100% editables como hoy.
function priceSemaforo(unitPrice, refPrecio) {
  if (!refPrecio || refPrecio <= 0 || !unitPrice) return null;
  const diffPct = ((Number(unitPrice) / refPrecio) - 1) * 100;
  const cls = diffPct > 1 ? "bg-red-50 border-red-300" : diffPct < -1 ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300";
  const textCls = diffPct > 1 ? "text-red-600" : diffPct < -1 ? "text-emerald-600" : "text-amber-600";
  return { diffPct, cls, textCls };
}

// CEN 2.0 FASE 3 PARTE B — colores de estado de consumo (reutilizado en varias celdas del AP)
const CONSUMO_ESTADO_TXT = { excedido: "text-red-600", por_agotarse: "text-amber-600", ok: "text-emerald-600" };

// BLOQUE 39 PARTE B — tabla del AP: 4 primeras columnas fijas (sticky left), 2 filas de
// encabezado fijas (sticky top) y barra de desplazamiento horizontal siempre visible.
// SOLO presentación: no cambia datos, cálculos ni endpoints.
const AP_TABLE_STICKY_CSS = `
.ap-tbl { border-collapse: separate; border-spacing: 0; }
.ap-tbl tbody td { border-top: 1px solid #f3f4f6; }
.ap-tbl .ap-c1, .ap-tbl .ap-c2, .ap-tbl .ap-c3, .ap-tbl .ap-c4 { position: sticky; z-index: 20; }
.ap-tbl .ap-c1 { left: 0; width: 190px; min-width: 190px; }
.ap-tbl .ap-c2 { left: 190px; width: 44px; min-width: 44px; }
.ap-tbl .ap-c3 { left: 234px; width: 64px; min-width: 64px; }
.ap-tbl .ap-c4 { left: 298px; width: 96px; min-width: 96px; box-shadow: 6px 0 8px -6px rgba(15,23,42,.45); }
.ap-tbl thead tr:first-child th { position: sticky; top: 0; z-index: 30; }
.ap-tbl thead tr:nth-child(2) th { position: sticky; top: var(--ap-h1, 44px); z-index: 30; }
.ap-tbl thead th.ap-c1, .ap-tbl thead th.ap-c2, .ap-tbl thead th.ap-c3, .ap-tbl thead th.ap-c4 { z-index: 45; }
.ap-tbl .ap-foot-c1 { position: sticky; left: 0; z-index: 20; }
.ap-topbar { height: 14px; scrollbar-width: thin; }
.ap-topbar::-webkit-scrollbar { height: 11px; }
.ap-topbar::-webkit-scrollbar-track { background: #e2e8f0; border-radius: 6px; }
.ap-topbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 6px; }
.ap-topbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
@media (max-width: 640px) {
  .ap-tbl .ap-c2, .ap-tbl .ap-c3, .ap-tbl .ap-c4 { left: auto; box-shadow: none; }
  .ap-tbl .ap-c1 { width: 120px; min-width: 120px; box-shadow: 6px 0 8px -6px rgba(15,23,42,.45); }
}
`;

// BLOQUE 18 PARTE B — allocations[] es la fuente de verdad para repartir la cantidad de
// un ítem entre MÚLTIPLES proveedores. Retro-compat: un AP cerrado antes de este bloque
// solo tiene `selected_idx` (un proveedor con el 100% de la cantidad) — se migra en
// memoria a un allocation equivalente, sin tocar nada en el backend hasta que se guarde.
const getItemAllocations = (item) => {
  const allocs = item?.allocations;
  if (Array.isArray(allocs)) return allocs;
  const idx = item?.selected_idx ?? -1;
  if (typeof idx === "number" && idx >= 0) {
    return [{ provider_idx: idx, quantity: item.quantity || 0 }];
  }
  return [];
};

// BLOQUE 45 PARTE A — mismo criterio que el backend (_providers_with_allocations,
// purchases.py): un proveedor solo cuenta para el denominador "X/Y proveedores
// cerrados" si tiene al menos 1 ítem con cantidad adjudicada (allocation > 0). Una
// columna sin ninguna adjudicación (ej. Electropar sin ganar nada) NUNCA debe
// impedir que el conteo llegue a "completo" — evita el desfasaje 7/8 vs 7/7 que
// hacía ver un acta ya cerrada como si le faltara un proveedor.
const countProvidersWithAllocations = (providerColumns, items) => {
  const idxs = new Set();
  (items || []).forEach(item => {
    getItemAllocations(item).forEach(a => {
      const idx = a?.provider_idx;
      const qty = Number(a?.quantity) || 0;
      if (typeof idx === "number" && idx >= 0 && idx < (providerColumns || []).length && qty > 0) {
        idxs.add(idx);
      }
    });
  });
  return idxs.size;
};

// Fix CK (2026-09-11): "en el análisis debe verse las observaciones y marcas que vienen desde
// el pedido de materiales" (Sergio, 11/09). Los AP nuevos las traen en cada ítem; en los ya
// creados se buscan en el PP de origen (mismo ítem del presupuesto y mismo material).
const _normPedido = (s) => (s || "").toString().trim().toLowerCase();
export function datosDelPedido(item, pp) {
  if (item.marca_recomendada || item.observacion) return { marca: item.marca_recomendada || "", obs: item.observacion || "" };
  const items = pp?.items || [];
  const src = items.find(i => item.item_uid && i.item_uid === item.item_uid && _normPedido(i.material_name) === _normPedido(item.item_name))
    || items.find(i => _normPedido(i.material_name) === _normPedido(item.item_name));
  return { marca: (src?.marca_recomendada || "").trim(), obs: (src?.observacion || "").trim() };
}
export function actConDatosDelPedido(act, supplierOrders) {
  const pp = (supplierOrders || []).find(p => p._id === act.pp_id);
  return { ...act, analysis_items: (act.analysis_items || []).map(it => {
    const d = datosDelPedido(it, pp);
    return { ...it, marca_recomendada: d.marca, observacion: d.obs };
  }) };
}
// Fix FD (2026-10-07): "en el análisis y en la impresión del mismo se siga visualizando las
// cantidades provistas desde el stock propio ya sea en Pedido a Proveedores como en análisis
// mismo" (CEN). Sale de las solicitudes de retiro del pedido (o del resumen del PP) y de las
// generadas desde el AP.
const RSI_ESTADO = { pendiente: "Pendiente", aprobado: "Aprobado", aprobado_parcial: "Aprobado parcial", rechazado: "Rechazado" };
export function stockPropioDe(act, supplierOrders, rsis) {
  if (!act) return [];
  const pp = (supplierOrders || []).find(p => p._id === act.pp_id);
  const pmId = act.pm_origen_id || pp?.pm_id || "";
  const fila = (i, etapa, r) => ({
    material_name: i.material_name || "", unit: i.unit || "", quantity: Number(i.quantity) || 0,
    inventario: [i.origen_material_code, i.origen_material_name].filter(Boolean).join(" · "),
    etapa, solicitud: r ? [r.number, r.movement_wd_number].filter(Boolean).join(" · ") : "",
    estado: r ? (RSI_ESTADO[r.status] || r.status || "") : "",
  });
  const out = [];
  const delPedido = (rsis || []).filter(r => !r.ap_id && pmId && r.pm_id === pmId && !r.deleted_at);
  if (delPedido.length) delPedido.forEach(r => (r.items || []).forEach(i => out.push(fila(i, "Pedido a Proveedores", r))));
  else (pp?.stock_coverage_summary?.items || []).forEach(i => out.push(fila(i, "Pedido a Proveedores", null)));
  (rsis || []).filter(r => r.ap_id === act._id && !r.deleted_at)
    .forEach(r => (r.items || []).forEach(i => out.push(fila(i, "Análisis", r))));
  return out;
}
function StockPropio({ filas }) {
  if (!filas.length) return null;
  return (
    <div className="rounded-xl border border-teal-200 bg-teal-50/40 overflow-hidden" data-testid="ap-stock-propio">
      <p className="px-3 py-2 text-xs font-bold text-teal-800 uppercase tracking-wide border-b border-teal-100">Provisto desde el stock propio</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-[10px] text-teal-700 uppercase">
            {["Descripción", "UM", "Cant.", "Material del inventario", "Etapa", "Solicitud de retiro", "Estado"].map(h => <th key={h} className="px-3 py-1.5 text-left font-semibold">{h}</th>)}
          </tr></thead>
          <tbody>
            {filas.map((f, i) => (
              <tr key={i} className="border-t border-teal-100" data-testid={`ap-stock-propio-${i}`}>
                <td className="px-3 py-1.5 font-medium text-gray-800">{f.material_name}</td>
                <td className="px-3 py-1.5 text-gray-500">{f.unit}</td>
                <td className="px-3 py-1.5 font-bold text-gray-800 tabular-nums">{f.quantity}</td>
                <td className="px-3 py-1.5 text-gray-600">{f.inventario || "—"}</td>
                <td className="px-3 py-1.5 text-gray-600">{f.etapa}</td>
                <td className="px-3 py-1.5 font-mono text-[11px] text-gray-600">{f.solicitud || "—"}</td>
                <td className="px-3 py-1.5 text-gray-600">{f.estado || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DatosDelPedido({ item, pp }) {
  const d = datosDelPedido(item, pp);
  // Fix EG (2026-09-25): la línea junta el material de varios ítems del presupuesto; el
  // PF la vuelve a repartir por ítem.
  const n = (item.lineas_pm || []).length;
  const reparto = n > 1 ? <span className="block text-[10px] font-normal text-gray-400 mt-0.5" data-testid="ap-item-lineas">Total de {n} ítems del presupuesto</span> : null;
  // Fix EJ (2026-09-27): sub-ítem agregado al modificar el pedido (misma imputación).
  const sub = item.sub_de_nombre ? <span className="block text-[10px] font-normal text-sky-700 mt-0.5" data-testid="ap-item-sub">Sub-ítem de «{item.sub_de_nombre}»</span> : null;
  if (!d.marca && !d.obs) return <>{sub}{reparto}</>;
  return (
    <>
      {sub}
      {reparto}
      <span className="block text-[10px] font-normal text-gray-500 mt-0.5 whitespace-normal" data-testid="ap-item-datos-pedido">
        {d.marca && <>Marca: <strong className="text-gray-700">{d.marca}</strong></>}
        {d.marca && d.obs ? " · " : ""}
        {d.obs && <>Obs.: {d.obs}</>}
      </span>
    </>
  );
}

export function AnalisisPresupuestoTab({
  budgetActs, supplierOrders, loading, onRefresh, userRole,
  listasClientes, listasObras, allBudgets,
  filterCliente, setFilterCliente, filterObra, setFilterObra, filterPresupuesto, setFilterPresupuesto,
}) {
  const [showNew, setShowNew] = useState(false);
  const [newPPId, setNewPPId] = useState("");
  const [creating, setCreating] = useState(false);
  const [activeAct, setActiveAct] = useState(null);
  const [localColumns, setLocalColumns] = useState([]);
  const [localItems, setLocalItems] = useState([]);
  const [localNotes, setLocalNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewAct, setViewAct] = useState(null);
  const [newProviderName, setNewProviderName] = useState("");
  const [showAddProvider, setShowAddProvider] = useState(false);
  // Listas autocomplete
  const [listasProvsSearch, setListasProvsSearch] = useState([]);
  const [showListasSugg, setShowListasSugg] = useState(false);
  const searchListasRef = useRef(null);
  // AI analysis modal
  const [aiModal, setAiModal] = useState({ open: false, providerIdx: 0, file: null, loading: false, results: null });
  const aiFileInputRef = useRef(null);
  // Modify Items modal
  const [showModify, setShowModify] = useState(false);
  const [modifyItems, setModifyItems] = useState([]);
  const [savingModify, setSavingModify] = useState(false);
  const [stockSnap, setStockSnap] = useState(null);   // Fix EJ (2026-09-27): ítems al abrir «Verificar stock»
  const [ccSnap, setCcSnap] = useState(null);   // Fix GC (2026-10-14): ítems al abrir «Compra desde Caja Chica»
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reopeningId, setReopeningId] = useState(null);
  const [pdfSelectAct, setPdfSelectAct] = useState(null); // BLOQUE 23 — AP para el que se abrió el modal de selección de proveedores del PDF
  // BLOQUE 50 PARTE A — Buscar + Estado + Urgencia
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [filterUrgency, setFilterUrgency] = useState("");
  // BLOQUE 36 — cierre/reapertura parcial POR PROVEEDOR
  const [closingProviderIdx, setClosingProviderIdx] = useState(null);
  const [reopeningProviderIdx, setReopeningProviderIdx] = useState(null);
  // BLOQUE 39 PARTE B — doble scrollbar sincronizada + alto de la 1ª fila de encabezado
  const topScrollRef = useRef(null);
  const mainScrollRef = useRef(null);
  const syncingRef = useRef(false);
  const [apScrollWidth, setApScrollWidth] = useState(0);
  const [apHeadRowH, setApHeadRowH] = useState(44);
  // CEN 2.0 FASE 3 PARTE B/C — consumo por ítem (para columna Presup./Acum.)
  const [apConsumoMap, setApConsumoMap] = useState({});
  // Fix EX (2026-10-05): rangos de aprobación (Compras → Rango p/ Aprobaciones).
  const [rangosAprob, setRangosAprob] = useState(null);
  const [aprobandoAP, setAprobandoAP] = useState(false);
  const [aprobandoProv, setAprobandoProv] = useState(null);   // Fix HI: columna del proveedor que se está aprobando
  useEffect(() => {
    axios.get(`${API}/settings/rangos-aprobacion`, { withCredentials: true })
      .then(({ data }) => setRangosAprob(data))
      .catch(() => setRangosAprob(null));
  }, []);
  // Fix FD (2026-10-07): solicitudes de retiro, para mostrar lo provisto desde el stock propio.
  const [rsis, setRsis] = useState([]);
  useEffect(() => {
    axios.get(`${API}/purchases/stock-withdrawals`, { withCredentials: true })
      .then(({ data }) => setRsis(Array.isArray(data) ? data : []))
      .catch(() => setRsis([]));
  }, [budgetActs]);

  const syncScroll = (from, to) => {
    if (syncingRef.current || !from || !to) return;
    syncingRef.current = true;
    to.scrollLeft = from.scrollLeft;
    requestAnimationFrame(() => { syncingRef.current = false; });
  };

  const canEdit = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras"]);
  const canDelete = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Compras", "Jefe de Compras"]);
  const openPPs = supplierOrders.filter(pp => !budgetActs.some(a => a.pp_id === pp._id));
  const actIsAbierta = (act) => act.status === "abierta" && (act.closed_providers || []).length === 0;
  const actIsParcial = (act) => act.status === "abierta" && (act.closed_providers || []).length > 0;
  const baseFilteredActs = budgetActs.filter(act => matchClienteObraPresupuesto(act, { filterCliente, filterObra, filterPresupuesto }, { listasClientes, listasObras }));
  const apStatusCounts = {
    abierta: baseFilteredActs.filter(actIsAbierta).length,
    parcial: baseFilteredActs.filter(actIsParcial).length,
    cerrada: baseFilteredActs.filter(act => act.status === "cerrada").length,
  };
  const filteredActsUnsorted = baseFilteredActs.filter(act => {
    if (filterStatus === "abierta" && !actIsAbierta(act)) return false;
    if (filterStatus === "parcial" && !actIsParcial(act)) return false;
    if (filterStatus === "cerrada" && act.status !== "cerrada") return false;
    if (filterUrgency && act.urgency !== filterUrgency) return false;
    if (search) {
      const s = search.toLowerCase();
      const hit = (act.number || "").toLowerCase().includes(s) || (act.project_name || "").toLowerCase().includes(s) ||
        (act.pp_number || "").toLowerCase().includes(s) ||
        (act.provider_columns || []).some(c => (c || "").toLowerCase().includes(s)) ||
        (act.analysis_items || []).some(i => (i.item_name || "").toLowerCase().includes(s));
      if (!hit) return false;
    }
    return true;
  });
  // BLOQUE 50 PARTE B — actas sin cerrar (abiertas/cierre parcial) arriba, cerradas abajo; más nueva primero.
  const filteredActs = sortPendingFirst(filteredActsUnsorted, act => act.status !== "cerrada");

  const handleDeleteAP = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/purchases/budget-acts/${deleteTarget._id}`, { withCredentials: true });
      toast.success(`AP ${deleteTarget.number} eliminado`);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar AP"); }
    finally { setDeleting(false); }
  };

  const handleReopenAP = async (act, closeViewAfter = false) => {
    // BLOQUE 22 PARTE B — revierte el AP cerrado a "abierta" (el backend hace
    // soft-delete de los PF sin OC asociados y bloquea si ya hay OC generada).
    if (!window.confirm(`¿Reabrir el análisis ${act.number}? ${act.pf_generated ? "El/los Presupuesto(s) Final(es) generado(s) sin Orden de Compra serán revertidos. " : ""}Podrá volver a seleccionar proveedores y repartir cantidades.`)) return;
    setReopeningId(act._id);
    try {
      await axios.put(`${API}/purchases/budget-acts/${act._id}/reopen`, {}, { withCredentials: true });
      toast.success(`Análisis ${act.number} reabierto — ya puede volver a analizarlo`);
      if (closeViewAfter) setActiveAct(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al reabrir el análisis"); }
    finally { setReopeningId(null); }
  };

  // BLOQUE 36 — "Cerrar con proveedor": congela esa columna YA (genera su PF) sin
  // esperar a que los demás proveedores estén listos. Envía el mismo payload que
  // "Guardar" normal (persiste todos los cambios pendientes de la tabla, no solo
  // la columna que se cierra).
  const handleCloseProvider = async (colIdx) => {
    const providerName = localColumns[colIdx];
    if (!window.confirm(`¿Cerrar el análisis con "${providerName}"? Se generará su Presupuesto Final ahora mismo y la columna quedará bloqueada para edición. Los demás proveedores siguen abiertos y editables.`)) return;
    setClosingProviderIdx(colIdx);
    try {
      const res = await axios.post(
        `${API}/purchases/budget-acts/${activeAct._id}/close-provider`,
        { provider_idx: colIdx, provider_columns: localColumns, analysis_items: localItems, notes: localNotes },
        { withCredentials: true }
      );
      // Fix DV (2026-09-22): el cierre ya se había hecho y la pantalla no lo reflejaba.
      // El backend devuelve el acta como está: se sincroniza y se avisa con su PF.
      if (res.data?.ya_estaba_cerrado) {
        toast.info(
          `"${providerName}" ya estaba cerrado${res.data.pf_number_existente ? ` — su PF es ${res.data.pf_number_existente}` : ""}. Se actualizó la pantalla.`,
          { duration: 6000 }
        );
        onRefresh();
        openForEdit(res.data);
        return;
      }
      if (res.data?.full_close_triggered) {
        const sinAdj = res.data.providers_without_allocations || [];
        toast.success(
          sinAdj.length > 0
            ? `Cierre parcial con "${providerName}" — se cerró el análisis completo. ${sinAdj.length === 1 ? `El proveedor ${sinAdj[0]} quedó` : `Los proveedores ${sinAdj.join(", ")} quedaron`} sin adjudicaciones.`
            : `Cierre parcial con "${providerName}" — se cerró el análisis completo y el presupuesto quedó aprobado.`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Cierre parcial con "${providerName}" — PF generado. Ya puede continuar con su Orden de Compra.`);
      }
      onRefresh();
      openForEdit(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cerrar con el proveedor"); }
    finally { setClosingProviderIdx(null); }
  };

  // BLOQUE 36 — reabre el cierre parcial de UN proveedor puntual (bloqueado si su PF
  // ya tiene Orden de Compra). Funciona con el AP "abierta" (parcial) o "cerrada" total.
  const handleReopenProvider = async (colIdx, providerName) => {
    if (!window.confirm(`¿Reabrir el cierre parcial de "${providerName}"? Si su Presupuesto Final no tiene Orden de Compra, será revertido y la columna volverá a ser editable.`)) return;
    setReopeningProviderIdx(colIdx);
    try {
      const res = await axios.put(`${API}/purchases/budget-acts/${activeAct._id}/reopen-provider/${colIdx}`, {}, { withCredentials: true });
      toast.success(`Cierre parcial de "${providerName}" revertido — la columna vuelve a ser editable`);
      onRefresh();
      openForEdit(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al reabrir el proveedor"); }
    finally { setReopeningProviderIdx(null); }
  };

  // BLOQUE 25 PARTE A — al hacer click en "Descargar PDF" siempre se abre el modal de
  // opciones de impresión (ítems + proveedores). Reemplaza el atajo del Bloque 23 (que
  // saltaba el modal con ≤1 proveedor) porque ahora también se pueden filtrar FILAS
  // (ítems) independientemente de cuántos proveedores tenga cargados el AP.
  const openPdfSelect = (act) => {
    if (!act) return;
    if ((act.analysis_items || []).length === 0) { generateAPPDF({ ...act, stock_propio: stockPropioDe(act, supplierOrders, rsis) }); return; }   // Fix FD
    setPdfSelectAct(act);
  };

  // Al confirmar el modal: genera el PDF solo con los ítems/proveedores tildados y (si se
  // excluyó alguno) registra la trazabilidad en el modification_log del AP. El AP en
  // la base de datos NUNCA se modifica — es solo un filtro de exportación.
  const handleConfirmPdfSelection = async ({ visibleItemIdxs, visibleProviderIdxs }) => {
    const act = pdfSelectAct;
    if (!act) return;
    generateAPPDF({ ...actConDatosDelPedido(act, supplierOrders), stock_propio: stockPropioDe(act, supplierOrders, rsis) }, { visibleItemIdxs, visibleProviderIdxs }); // Fix CK · Fix FD
    setPdfSelectAct(null);
    const totalItems = (act.analysis_items || []).length;
    const totalProviders = (act.provider_columns || []).length;
    const excludedItemsCount = totalItems - visibleItemIdxs.length;
    const excludedProvidersCount = totalProviders - visibleProviderIdxs.length;
    if (excludedItemsCount > 0 || excludedProvidersCount > 0) {
      try {
        await axios.post(`${API}/purchases/budget-acts/${act._id}/log-pdf-export`, {
          included_count: visibleProviderIdxs.length,
          excluded_count: excludedProvidersCount,
          included_items_count: visibleItemIdxs.length,
          excluded_items_count: excludedItemsCount,
        }, { withCredentials: true });
      } catch { /* trazabilidad no bloqueante */ }
    }
  };

  const handleCreateAP = async (ppId) => {
    const id = ppId || newPPId;
    if (!id) { toast.error("Seleccione un PP"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/purchases/budget-acts`, { pp_id: id }, { withCredentials: true });
      toast.success("Análisis creado — ahora agregue proveedores y precios");
      setShowNew(false);
      setNewPPId("");
      onRefresh();
      openForEdit(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setCreating(false); }
  };

  // BLOQUE 14 PARTE A — id estable de columna: cada price lleva `provider` (nombre de la
  // columna). Al cargar, se realinea por nombre cuando existe; fallback posicional para
  // APs legacy sin el campo. Evita que precios aplicados a un proveedor terminen en otra columna.
  const alignPricesToColumns = (item, cols) => cols.map((colName, ci) => {
    const arr = item.prices || [];
    const byName = arr.find(p => p && p.provider === colName);
    return byName ? { ...byName } : { ...(arr[ci] || { unit_price: 0, obs: "" }), provider: colName };
  });

  const openForEdit = (act) => {
    const cols = act.provider_columns || [];
    setActiveAct(act);
    setLocalColumns([...cols]);
    setLocalItems((act.analysis_items || []).map(item => ({
      ...item,
      prices: alignPricesToColumns(item, cols),
      allocations: getItemAllocations(item).map(a => ({ provider_idx: a.provider_idx, quantity: Number(a.quantity) || 0 })),
    })));
    setLocalNotes(act.notes || "");
    // CEN 2.0 FASE 3 PARTE B — consumo por ítem del presupuesto (Presup./Acum.)
    if (act.presupuesto_id) {
      axios.get(`${API}/budgets/${act.presupuesto_id}/consumo`, { withCredentials: true })
        .then(({ data }) => {
          const m = {};
          (data || []).forEach(row => { m[row.item_uid] = row; });
          setApConsumoMap(m);
        })
        .catch(() => setApConsumoMap({}));
    } else {
      setApConsumoMap({});
    }
  };

  // BLOQUE 39 PARTE B — mide el ancho scrolleable (para la barra superior) y el alto de
  // la 1ª fila de encabezado (para el offset sticky de la 2ª fila).
  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    const measure = () => {
      setApScrollWidth(el.scrollWidth);
      const r1 = el.querySelector("thead tr");
      if (r1) setApHeadRowH(Math.round(r1.getBoundingClientRect().height));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const tbl = el.querySelector("table");
    if (tbl) ro.observe(tbl);
    return () => ro.disconnect();
  }, [activeAct?._id, localColumns.length, localItems.length]);


  const addProviderColumn = () => {
    const trimmed = newProviderName.trim();
    if (!trimmed) { toast.error("Ingrese un nombre de proveedor"); return; }
    if (localColumns.includes(trimmed)) { toast.error("Proveedor ya existe"); return; }
    setLocalColumns(prev => [...prev, trimmed]);
    setLocalItems(prev => prev.map(item => ({
      ...item,
      prices: [...(item.prices || []), { unit_price: 0, obs: "", provider: trimmed }]
    })));
    setNewProviderName("");
    setShowAddProvider(false);
  };

  // Fix DV (2026-09-22): al borrar una columna se corrían los precios y las
  // adjudicaciones, pero NO los cierres parciales, así que el chip "Cerrado" quedaba
  // sobre la columna equivocada y la que faltaba cerrar respondía "ya fue cerrado".
  const removeProviderColumn = (ci) => {
    const cerrados = activeAct?.closed_providers || [];
    if (cerrados.some(cp => cp.provider_idx === ci)) {
      toast.error("No se puede quitar la columna de un proveedor ya cerrado: primero use «Reabrir proveedor»");
      return;
    }
    setLocalColumns(prev => prev.filter((_, i) => i !== ci));
    setLocalItems(prev => prev.map(item => ({
      ...item,
      prices: (item.prices || []).filter((_, i) => i !== ci),
      allocations: (item.allocations || [])
        .filter(a => a.provider_idx !== ci)
        .map(a => a.provider_idx > ci ? { ...a, provider_idx: a.provider_idx - 1 } : a),
    })));
    if (cerrados.length) {
      setActiveAct(prev => prev && ({
        ...prev,
        closed_providers: (prev.closed_providers || []).map(cp =>
          cp.provider_idx > ci ? { ...cp, provider_idx: cp.provider_idx - 1 } : cp),
      }));
    }
  };

  const updatePrice = (itemIdx, colIdx, field, value) => {
    setLocalItems(prev => {
      const items = [...prev];
      const prices = [...(items[itemIdx].prices || [])];
      prices[colIdx] = {
        ...(prices[colIdx] || { unit_price: 0, obs: "" }),
        provider: localColumns[colIdx],
        [field]: field === "unit_price" ? Number(value) : value
      };
      items[itemIdx] = { ...items[itemIdx], prices };
      return items;
    });
  };

  const updateQuantity = (itemIdx, value) => {
    setLocalItems(prev => {
      const items = [...prev];
      items[itemIdx] = { ...items[itemIdx], quantity: Number(value) || 1 };
      return items;
    });
  };

  const searchListasProvs = async (q) => {
    if (q.length < 2) { setListasProvsSearch([]); setShowListasSugg(false); return; }
    try {
      const res = await axios.get(`${API}/listas/proveedores/search?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setListasProvsSearch(res.data.slice(0, 8));
      setShowListasSugg(true);
    } catch { setListasProvsSearch([]); }
  };

  const selectProvider = (itemIdx, colIdx) => {
    // BLOQUE 18 PARTE B — el botón "Sel." pasa a ser un toggle de INCLUSIÓN (no
    // exclusivo entre columnas): activarlo agrega el proveedor a allocations[] del
    // ítem con la cantidad restante sin asignar como sugerencia (editable); desactivarlo
    // lo quita. Puede haber más de un proveedor activo por ítem simultáneamente.
    setLocalItems(prev => {
      const items = [...prev];
      const item = items[itemIdx];
      const allocations = item.allocations || [];
      const exists = allocations.some(a => a.provider_idx === colIdx);
      let newAllocations;
      if (exists) {
        newAllocations = allocations.filter(a => a.provider_idx !== colIdx);
      } else {
        const assigned = allocations.reduce((s, a) => s + (Number(a.quantity) || 0), 0);
        const remaining = Math.max((item.quantity || 0) - assigned, 0);
        newAllocations = [...allocations, { provider_idx: colIdx, quantity: remaining }];
      }
      items[itemIdx] = { ...item, allocations: newAllocations };
      return items;
    });
  };

  const updateAllocationQty = (itemIdx, colIdx, value) => {
    setLocalItems(prev => {
      const items = [...prev];
      const item = items[itemIdx];
      const allocations = (item.allocations || []).map(a =>
        a.provider_idx === colIdx ? { ...a, quantity: Number(value) || 0 } : a
      );
      items[itemIdx] = { ...item, allocations };
      return items;
    });
  };

  const hasAnySelection = localItems.some(it => (it.allocations || []).some(a => (Number(a.quantity) || 0) > 0) || (typeof it.selected_idx === "number" && it.selected_idx >= 0));

  // Fix EJ (2026-09-27): "que exista en el análisis de presupuestos un botón de mejor
  // combinación donde automático marca la mejor combinación" (CEN). Cada ítem va entero al
  // proveedor con el menor P.U. cargado. No toca las columnas cerradas ni los ítems ya
  // adjudicados a un proveedor cerrado. Queda para revisar y guardar.
  const aplicarMejorCombinacion = () => {
    const cerrados = (activeAct?.closed_providers || []).map(cp => cp.provider_idx);
    if (localItems.some(it => (it.allocations || []).length > 0) && !window.confirm("Se reemplaza la selección actual por el menor precio de cada ítem. ¿Continuar?")) return;
    let n = 0;
    setLocalItems(localItems.map(it => {
      if ((it.allocations || []).some(a => cerrados.includes(a.provider_idx))) return it;
      let mejor = -1;
      localColumns.forEach((_, ci) => {
        const pu = Number((it.prices || [])[ci]?.unit_price) || 0;
        if (pu > 0 && !cerrados.includes(ci) && (mejor < 0 || pu < Number(it.prices[mejor].unit_price))) mejor = ci;
      });
      if (mejor < 0) return it;
      n += 1;
      return { ...it, allocations: [{ provider_idx: mejor, quantity: Number(it.quantity) || 0 }] };
    }));
    if (n) toast.success(`Mejor combinación aplicada en ${n} ítem(s) — revisá y guardá`);
    else toast.error("No hay precios cargados para elegir");
  };

  const handleSave = async (close = false) => {
    if (close) {
      // BLOQUE 22 PARTE A — no se puede cerrar sin al menos 1 ítem con proveedor
      // seleccionado (el backend también lo valida; este check evita el round-trip).
      if (!hasAnySelection) {
        toast.error("No se puede cerrar el análisis: al menos un ítem debe tener un proveedor seleccionado");
        return;
      }
      // BLOQUE 18 PARTE B — advertencia NO bloqueante si algún ítem queda con cantidad
      // acumulada distinta a la solicitada (compra parcial/sobrecompra intencional).
      const mismatched = localItems.filter(it => {
        const acumulada = (it.allocations || []).reduce((s, a) => s + (Number(a.quantity) || 0), 0);
        return acumulada !== (it.quantity || 0);
      }).length;
      if (mismatched > 0 && !window.confirm(`Hay ${mismatched} ítem(s) con la cantidad asignada distinta a la solicitada (cant. acumulada en rojo o verde). ¿Desea cerrar el AP igualmente?`)) {
        return;
      }
    }
    setSaving(true);
    const payload = { provider_columns: localColumns, analysis_items: localItems, notes: localNotes };
    try {
      const url = close
        ? `${API}/purchases/budget-acts/${activeAct._id}/close`
        : `${API}/purchases/budget-acts/${activeAct._id}`;
      const { data } = await axios.put(url, payload, { withCredentials: true });
      // Fix EX (2026-10-05): "no se llegó a generar el PF" (CEN). Al cerrar se generan los PF
      // en el mismo paso (antes había que ir a Ptos. Finales → Generar PF). Si el AP ya estaba
      // cerrado (pantalla sin refrescar), se avisa así y también se generan los que falten.
      let pfs = [];
      if (close) {
        try {
          const r = await axios.post(`${API}/purchases/final-budgets/from-ap/${activeAct._id}`, {}, { withCredentials: true });
          pfs = (r.data || []).map(pf => pf.number).filter(Boolean);
        } catch { /* queda "PF pendiente de generar" y se genera desde Ptos. Finales */ }
      }
      toast.success(close
        ? `${data?.ya_estaba_cerrado ? "Este AP ya estaba cerrado" : "Acta de Presupuesto cerrada exitosamente"}${pfs.length ? ` · PF: ${pfs.join(", ")}` : ""}`
        : "Análisis guardado");
      if (close) setActiveAct(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  // Fix EX (2026-10-05): aprobar el análisis por su rango de monto. En el editor se guarda
  // antes, porque se aprueba lo guardado; desde "Ver acta" se aprueba tal como está.
  const aprobarAP = async (desdeVista) => {
    const act = desdeVista ? viewAct : activeAct;
    if (!act) return;
    setAprobandoAP(true);
    try {
      if (!desdeVista) {
        await axios.put(`${API}/purchases/budget-acts/${act._id}`,
          { provider_columns: localColumns, analysis_items: localItems, notes: localNotes }, { withCredentials: true });
      }
      const { data } = await axios.post(`${API}/purchases/budget-acts/${act._id}/aprobar`, {}, { withCredentials: true });
      if (desdeVista) setViewAct(data);
      else setActiveAct(a => ({ ...a, aprobaciones_ap: data.aprobaciones_ap }));
      toast.success("Aprobación registrada");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al aprobar"); }
    finally { setAprobandoAP(false); }
  };

  // Fix HI (2026-10-21): "debe ser aprobación por cada cierre parcial, no aprobar la operación con los datos del momento"
  // (CEN). Aprueba el cierre con UN proveedor, por el monto de su columna (se guarda antes, porque se aprueba lo guardado).
  const aprobarProveedor = async (colIdx) => {
    if (!activeAct) return;
    setAprobandoProv(colIdx);
    try {
      await axios.put(`${API}/purchases/budget-acts/${activeAct._id}`,
        { provider_columns: localColumns, analysis_items: localItems, notes: localNotes }, { withCredentials: true });
      const { data } = await axios.post(`${API}/purchases/budget-acts/${activeAct._id}/aprobar`, { provider_idx: colIdx }, { withCredentials: true });
      setActiveAct(a => ({ ...a, aprobaciones_ap_prov: data.aprobaciones_ap_prov }));
      toast.success(`Aprobación registrada para el cierre con «${localColumns[colIdx]}»`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al aprobar"); }
    finally { setAprobandoProv(null); }
  };

  const openModifyModal = () => {
    setModifyItems(localItems.map(i => ({ ...i })));
    setShowModify(true);
  };

  const updateModifyItem = (idx, field, value) => {
    setModifyItems(prev => {
      const items = [...prev];
      items[idx] = { ...items[idx], [field]: field === "quantity" ? (Number(value) || 1) : value };
      return items;
    });
  };

  const addModifyItem = () => {
    setModifyItems(prev => [...prev, {
      item_name: "",
      unit: "un",
      quantity: 1,
      sector: "",
      front: "",
      prices: localColumns.map(() => ({ unit_price: 0, obs: "" })),
      allocations: [],
      _isNew: true
    }]);
  };

  const removeModifyItem = (idx) => {
    setModifyItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Fix EJ (2026-09-27): "la línea editada debe seguir siendo imputada a los ítems asignados
  // al momento del Pedido Original ... al agregar un nuevo ítem, se agregue anexado a un ítem"
  // (CEN). El sub-ítem va debajo de su línea y hereda su imputación (ítem, nivel, categoría y
  // el reparto por ítem del presupuesto), así llega igual al PF y a la OC.
  const addSubItem = (idx) => {
    setModifyItems(prev => {
      const padre = prev[idx];
      let fin = idx + 1;
      while (fin < prev.length && prev[fin].sub_de_item_id && prev[fin].sub_de_item_id === padre.item_id) fin++;
      const hereda = {};
      ["item_uid", "categoria", "nivel_id", "tarea_numero", "sector", "front", "dias_uso_estimado", "lineas_pm"].forEach(k => {
        if (padre[k] !== undefined && padre[k] !== null) hereda[k] = padre[k];
      });
      const nuevo = {
        ...hereda, item_id: `sub-${Date.now()}-${fin}`, item_name: "", unit: padre.unit || "un", quantity: 1,
        prices: localColumns.map(c => ({ unit_price: 0, obs: "", provider: c })), selected_idx: -1, allocations: [],
        sub_de_item_id: padre.item_id || "", sub_de_nombre: padre.item_name || "", _sub: true,
      };
      return [...prev.slice(0, fin), nuevo, ...prev.slice(fin)];
    });
  };

  const handleSaveModify = async () => {
    if (modifyItems.some(i => i.sub_de_item_id !== undefined && !String(i.item_name || "").trim())) {
      toast.error("Completá la descripción de los sub-ítems");
      return;
    }
    setSavingModify(true);
    try {
      // Split into new (no item_id, _isNew=true) and updated (existing items kept/edited).
      // Sending new items in `new_items` array makes the backend log them as ADDED
      // instead of being incorrectly matched-by-position to an existing item_id (rename bug).
      const newItems = modifyItems.filter(i => i._isNew || !i.item_id).map(({ _isNew, item_id, ...i }) => i);
      const updatedItems = modifyItems.filter(i => !i._isNew && i.item_id).map(({ _isNew, _sub, ...i }) => i);
      const res = await axios.post(
        `${API}/purchases/budget-acts/${activeAct._id}/modify-items`,
        { new_items: newItems, updated_items: updatedItems },
        { withCredentials: true }
      );
      toast.success("Pedido modificado exitosamente");
      setShowModify(false);
      // Refresh local state from response
      const updated = res.data;
      setLocalItems((updated.analysis_items || []).map(item => ({
        ...item,
        prices: localColumns.map((_, ci) => (item.prices || [])[ci] || { unit_price: 0, obs: "" }),
        allocations: getItemAllocations(item).map(a => ({ provider_idx: a.provider_idx, quantity: Number(a.quantity) || 0 })),
      })));
      setActiveAct(prev => ({ ...prev, modification_log: updated.modification_log || [] }));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al modificar"); }
    finally { setSavingModify(false); }
  };

  // ---- AI ANALYSIS ----
  const openAIModal = () => {
    setAiModal({ open: true, providerIdx: 0, file: null, loading: false, results: null });
  };

  const handleAIFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) {
      toast.error("Solo se permiten archivos PDF, PNG, JPG o WEBP");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("El archivo no puede superar 20 MB");
      return;
    }
    setAiModal(prev => ({ ...prev, file, results: null }));
  };

  const handleAIAnalyze = () => {
    if (!aiModal.file) { toast.error("Cargue un documento primero"); return; }
    setAiModal(prev => ({ ...prev, loading: true, results: null }));
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const res = await axios.post(
          `${API}/purchases/budget-acts/${activeAct._id}/ai-analyze`,
          { file_data: ev.target.result, file_name: aiModal.file.name, file_type: aiModal.file.type },
          { withCredentials: true }
        );
        // BLOQUE 14 PARTE A — auto-selección de columna destino: si la IA detecta que la
        // cotización pertenece a OTRO proveedor de la lista, se corrige la columna destino
        // automáticamente (el usuario suele dejar el select en el 1er proveedor por defecto).
        const detected = res.data?.proveedor_sugerido;
        setAiModal(prev => {
          let idx = prev.providerIdx;
          if (detected) {
            const matchIdx = localColumns.findIndex(c => _namesMatch(c, detected));
            if (matchIdx >= 0 && matchIdx !== idx) {
              idx = matchIdx;
              toast.info(`La cotización pertenece a "${localColumns[matchIdx]}" — columna destino actualizada automáticamente`);
            }
          }
          return { ...prev, loading: false, results: res.data, providerIdx: idx };
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al analizar el documento");
        setAiModal(prev => ({ ...prev, loading: false }));
      }
    };
    reader.readAsDataURL(aiModal.file);
  };

  const handleApplyAIPrices = () => {
    const { providerIdx, results } = aiModal;
    // BLOQUE 14 PARTE A — columna destino por id ESTABLE (nombre del proveedor),
    // nunca por posición: los índices posicionales causaban que los precios cayeran
    // en la columna del primer proveedor.
    const providerName = localColumns[providerIdx];
    if (!providerName) { toast.error("Seleccione un proveedor destino"); return; }
    if (!results?.precios_extraidos?.length) { toast.error("No hay precios para aplicar"); return; }
    // Resolver destinos ANTES del setState (el updater es diferido — el contador del
    // toast se leía en 0 aunque los precios sí se aplicaran).
    const updates = [];
    results.precios_extraidos.forEach(extracted => {
      const idx = _resolveAiTargetIdx(extracted, localItems);
      if (idx >= 0 && idx < localItems.length && extracted.precio_unitario !== null && extracted.precio_unitario !== undefined) {
        updates.push({ idx, precio: extracted.precio_unitario });
      }
    });
    if (!updates.length) { toast.error("No hay precios para aplicar"); return; }
    setLocalItems(prev => {
      const items = [...prev];
      updates.forEach(({ idx, precio }) => {
        if (idx >= items.length) return;
        const prices = [...(items[idx].prices || [])];
        let ci = prices.findIndex(p => p && p.provider === providerName);
        if (ci < 0) ci = providerIdx;
        prices[ci] = { ...(prices[ci] || { unit_price: 0, obs: "" }), unit_price: precio, provider: providerName };
        items[idx] = { ...items[idx], prices };
      });
      return items;
    });
    toast.success(`${updates.length} precio(s) aplicados a "${providerName}"`);
    setAiModal({ open: false, providerIdx: 0, file: null, loading: false, results: null });
  };

  const providerTotals = localColumns.map((_, ci) =>
    localItems.reduce((sum, item) => {
      const alloc = (item.allocations || []).find(a => a.provider_idx === ci);
      const qty = alloc ? (Number(alloc.quantity) || 0) : 0;
      const price = (item.prices || [])[ci]?.unit_price || 0;
      return sum + price * qty;
    }, 0)
  );

  // BLOQUE 18 PARTE B — Σ (cantidad asignada × P.U.) sobre TODOS los allocations[] de
  // cada ítem (soporta reparto entre múltiples proveedores, no solo el ganador único).
  const selectedTotal = localItems.reduce((sum, item) => {
    return sum + (item.allocations || []).reduce((s, a) => {
      const price = (item.prices || [])[a.provider_idx]?.unit_price || 0;
      return s + price * (Number(a.quantity) || 0);
    }, 0);
  }, 0);

  // ---- EDIT MODE ----
  if (activeAct) {
    // BLOQUE 36 — el AP puede llegar aquí "cerrada" TOTALMENTE (para reabrir un
    // proveedor puntual) o "abierta" (clásico o con cierres parciales en curso).
    const isActFullyClosed = activeAct.status === "cerrada";
    const closedProviders = activeAct.closed_providers || [];
    const isProviderClosed = (ci) => closedProviders.some(cp => cp.provider_idx === ci);
    const getClosedEntry = (ci) => closedProviders.find(cp => cp.provider_idx === ci);
    // Fix HI (2026-10-21): con dos o más proveedores abiertos con cantidad, cada uno se aprueba en su columna.
    const actEditor = { ...activeAct, provider_columns: localColumns, analysis_items: localItems };
    const aprobacionPorProveedor = !isActFullyClosed && proveedoresAbiertosAP(actEditor).length >= 2;
    // Fix EJ (2026-09-27): numeración del modal Modificar Pedido (los sub-ítems: 1.1, 1.2…).
    let nroPadre = 0, nroSub = 0;
    const numerosModify = modifyItems.map(it => {
      if (it.sub_de_item_id !== undefined) { nroSub += 1; return `${nroPadre}.${nroSub}`; }
      nroPadre += 1; nroSub = 0; return String(nroPadre);
    });
    // CEN 2.0 FASE 3 PARTE C — sobreprecio agregado: Σ (P.U. - ref) × cantidad adjudicada,
    // solo para adjudicaciones con precio por encima del costo de referencia (+1%).
    const apSobreprecioAlert = localItems.reduce((acc, item) => {
      const ref = item.ref_precio_mat;
      if (!ref) return acc;
      (item.allocations || []).forEach(a => {
        const up = Number((item.prices || [])[a.provider_idx]?.unit_price) || 0;
        if (up > ref * 1.01) {
          acc.total += (up - ref) * (Number(a.quantity) || 0);
          acc.count += 1;
        }
      });
      return acc;
    }, { total: 0, count: 0 });
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <button onClick={() => setActiveAct(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors" title="Volver a la lista">
                <ArrowRight size={16} className="rotate-180" />
              </button>
              <p className="font-bold text-gray-900">{activeAct.number}</p>
              {/* BLOQUE 45 PARTE A — fuente ÚNICA de verdad: act.status (antes este
                  badge quedaba hardcodeado en "Análisis Ppto." sin importar si el
                  cierre total ya había sido disparado por el cierre del último
                  proveedor con adjudicaciones — causaba el mensaje contradictorio
                  reportado por el cliente justo al terminar de cerrar). */}
              <StatusBadge status={isActFullyClosed ? "presupuesto_aprobado" : "analisis_presupuesto"} />
              {isActFullyClosed && <BadgeCheck size={15} className="text-emerald-500" data-testid="ap-detail-closed-check" />}
              {/* La píldora de avance solo tiene sentido mientras el acta sigue
                  abierta; una vez cerrada se reemplaza por el check de arriba. El
                  denominador cuenta solo proveedores CON adjudicaciones (mismo
                  criterio del backend), nunca todas las columnas. */}
              {!isActFullyClosed && closedProviders.length > 0 && (
                <span className="text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full" data-testid="ap-partial-badge">
                  {closedProviders.length}/{countProvidersWithAllocations(localColumns, localItems)} proveedores cerrados
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5 ml-9">{activeAct.project_name} · PP: {activeAct.pp_number}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Fix GM (2026-10-16): "En los análisis podemos ver también de agregar la opción de imprimir
                dentro del análisis" (CEN). Mismo PDF y mismas opciones que «Descargar PDF» de la lista,
                con lo que se ve en pantalla (incluye los cambios sin guardar). */}
            <button onClick={() => openPdfSelect({ ...activeAct, provider_columns: localColumns, analysis_items: localItems })}
              title="Imprimir el análisis (PDF)"
              className="flex items-center gap-1.5 border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-gray-50 transition-all"
              data-testid="ap-imprimir-btn">
              <Printer size={12} /> Imprimir
            </button>
            {isActFullyClosed && canDelete && !activeAct.cubierto_por_stock && (
              <button onClick={() => handleReopenAP(activeAct, true)} disabled={reopeningId === activeAct._id}
                title="Reabrir el análisis completo (todos los proveedores)"
                className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50 transition-all shadow-sm"
                data-testid="reopen-full-ap-btn">
                {reopeningId === activeAct._id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Reabrir análisis completo
              </button>
            )}
            {!isActFullyClosed && (<>
            <button onClick={openModifyModal}
              className="flex items-center gap-1.5 border border-dashed border-amber-400 text-amber-600 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-amber-50 transition-all"
              data-testid="modify-pedido-btn">
              <Pencil size={12} /> Modificar Pedido
            </button>
            <button onClick={() => setShowAddProvider(true)}
              className="flex items-center gap-1.5 border border-dashed border-[#2E6DA4] text-[#2E6DA4] px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-50 transition-all"
              data-testid="add-provider-col-btn">
              <Plus size={12} /> Proveedor
            </button>
            {/* Fix EJ (2026-09-27): verificar stock y mejor combinación */}
            <button onClick={() => setStockSnap(localItems)}
              className="flex items-center gap-1.5 border border-amber-400 text-amber-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-amber-50 transition-all"
              data-testid="ap-verificar-stock-btn">
              <Boxes size={12} /> Verificar stock
            </button>
            {/* Fix GC (2026-10-14): "Compra desde caja chica" (CEN) */}
            {canEdit && (
              <button onClick={() => setCcSnap(localItems)}
                className="flex items-center gap-1.5 border border-amber-400 text-amber-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-amber-50 transition-all"
                data-testid="ap-compra-cc-btn">
                <Wallet size={12} /> Compra desde caja chica
              </button>
            )}
            {localColumns.length > 0 && (
              <button onClick={aplicarMejorCombinacion}
                title="Adjudica cada ítem entero al proveedor con el menor P.U. cargado"
                className="flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                data-testid="ap-mejor-combinacion-btn">
                <Sparkles size={13} /> Mejor combinación
              </button>
            )}
            {localColumns.length > 0 && (
              <button onClick={openAIModal}
                className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shadow-sm"
                data-testid="ai-analyze-doc-btn">
                <Brain size={13} /> Analizar con IA
              </button>
            )}
            {localColumns.length > 0 && (
              <button
                onClick={() => {
                  if (!window.confirm("¿Eliminar TODAS las cotizaciones cargadas? Los precios y proveedores serán borrados, los ítems se mantienen.")) return;
                  setLocalColumns([]);
                  setLocalItems(localItems.map(it => ({ ...it, prices: [], allocations: [] })));
                  toast.success("Cotizaciones eliminadas — guarde los cambios");
                }}
                className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                data-testid="clear-quotations-btn">
                <X size={13} /> Eliminar Cotizaciones
              </button>
            )}
            <button onClick={() => handleSave(false)} disabled={saving}
              data-testid="save-ap-btn"
              className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50 transition-all">
              {saving ? <Loader2 size={13} className="animate-spin inline mr-1" /> : null}Guardar
            </button>
            <button onClick={() => handleSave(true)} disabled={saving || !hasAnySelection}
              title={!hasAnySelection ? "Seleccione al menos un proveedor en algún ítem antes de cerrar" : "Cerrar el Análisis de Presupuesto"}
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="close-ap-btn">
              <BadgeCheck size={13} /> Cerrar AP
            </button>
            </>)}
          </div>
        </div>

        {/* Fix EX (2026-10-05): aprobación por rango de monto */}
        {!isActFullyClosed && (
          <AprobacionAP act={actEditor}
            cfg={rangosAprob} onAprobar={() => aprobarAP(false)} trabajando={aprobandoAP} />
        )}

        {isActFullyClosed && activeAct.cubierto_por_stock && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 flex items-center gap-2.5 text-xs text-teal-800" data-testid="ap-cubierto-stock-banner">
            <Boxes size={14} className="text-teal-600 flex-shrink-0" />
            {activeAct.cubierto_por_caja_chica
              ? "Todo el pedido se cubrió con stock interno y compra por Caja Chica: el análisis quedó cerrado, sin Presupuesto Final. Las compras siguen en Compras → Caja Chica (aprobar, pagar y cargar el comprobante)."
              : "Todo el pedido sale del stock interno: el análisis quedó cerrado, sin Presupuesto Final. El retiro sigue en Inventario → Pedido de Movimiento."}
          </div>
        )}
        {isActFullyClosed && !activeAct.cubierto_por_stock && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-2.5 text-xs text-gray-600" data-testid="ap-fully-closed-readonly-banner">
            <BadgeCheck size={14} className="text-emerald-500 flex-shrink-0" />
            AP totalmente cerrado — modo solo lectura. Si necesita corregir un proveedor puntual, use «Reabrir proveedor» en su columna (bloqueado si ya tiene Orden de Compra); o «Reabrir análisis completo» para volver a analizar todo.
          </div>
        )}

        {/* Add provider inline form with Listas autocomplete */}
        {!isActFullyClosed && showAddProvider && (
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 flex items-start gap-3">
            <div className="flex-1 relative">
              <input
                value={newProviderName}
                onChange={e => { setNewProviderName(e.target.value); searchListasProvs(e.target.value); }}
                onKeyDown={e => { if (e.key === "Enter") { addProviderColumn(); } if (e.key === "Escape") { setShowListasSugg(false); } }}
                onBlur={() => setTimeout(() => setShowListasSugg(false), 200)}
                placeholder="Nombre del proveedor — busca en Catálogo Listas (ej: Electropar, ABB)"
                className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20"
                autoFocus
                data-testid="provider-name-input"
              />
              {/* Listas suggestions dropdown */}
              {showListasSugg && listasProvsSearch.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-xl z-50 overflow-hidden">
                  <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Catálogo de Listas</p>
                  </div>
                  {listasProvsSearch.map((prov, idx) => (
                    <button key={idx} type="button"
                      onMouseDown={() => { setNewProviderName(prov.proveedor || ""); setShowListasSugg(false); setListasProvsSearch([]); }}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between gap-2 group transition-colors">
                      <div>
                        <p className="text-sm font-medium text-gray-800 group-hover:text-[#2E6DA4]">{prov.proveedor}</p>
                        <p className="text-[10px] text-gray-400">{prov.rubro || "—"} · RUC: {prov.ruc || "—"}</p>
                      </div>
                      <span className="font-mono text-[10px] font-bold bg-[#2E6DA4]/10 text-[#2E6DA4] px-2 py-0.5 rounded-full flex-shrink-0">
                        {prov.codigo}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={addProviderColumn} className="bg-[#2E6DA4] text-white px-3 py-2 rounded-lg text-xs font-semibold flex-shrink-0" data-testid="confirm-add-provider-btn">
              Agregar
            </button>
            <button onClick={() => { setShowAddProvider(false); setNewProviderName(""); setShowListasSugg(false); }}
              className="p-2 rounded-lg hover:bg-blue-100 text-blue-500 flex-shrink-0">
              <X size={14} />
            </button>
          </div>
        )}

        {/* CEN 2.0 FASE 3 PARTE C — alerta agregada de sobreprecio (informativa, no bloqueante) */}
        {apSobreprecioAlert.count > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2.5" data-testid="ap-sobreprecio-alert">
            <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-700">
              <strong>{apSobreprecioAlert.count} ítem{apSobreprecioAlert.count !== 1 ? "s" : ""}</strong> adjudicado{apSobreprecioAlert.count !== 1 ? "s" : ""} por encima del costo de referencia del presupuesto — sobreprecio estimado: <strong>{formatPYG(apSobreprecioAlert.total)}</strong>.
            </p>
          </div>
        )}

        {/* Analysis grid */}
        {localColumns.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
            <BarChart2 size={36} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">Agregue proveedores para comparar precios</p>
            <p className="text-xs text-gray-400 mt-1">Haga clic en «+ Proveedor» para añadir la primera columna</p>
            <button onClick={() => setShowAddProvider(true)}
              className="mt-4 flex items-center gap-2 bg-[#2E6DA4] text-white px-4 py-2 rounded-xl text-xs font-semibold mx-auto transition-all hover:bg-[#245A8A]"
              data-testid="add-first-provider-btn">
              <Plus size={14} /> Agregar Proveedor
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden" data-testid="ap-analysis-table-card">
            <style>{AP_TABLE_STICKY_CSS}</style>
            {/* BLOQUE 39 PARTE B — barra de desplazamiento horizontal siempre visible (sincronizada) */}
            <div ref={topScrollRef}
              onScroll={() => syncScroll(topScrollRef.current, mainScrollRef.current)}
              className="ap-topbar overflow-x-auto overflow-y-hidden bg-gray-50 border-b border-gray-200"
              data-testid="ap-top-scrollbar">
              <div style={{ width: apScrollWidth || "100%", height: 1 }} />
            </div>
            <div ref={mainScrollRef}
              onScroll={() => syncScroll(mainScrollRef.current, topScrollRef.current)}
              className="overflow-auto max-h-[70vh]"
              data-testid="ap-table-scroll">
            <table className="ap-tbl text-xs" style={{ minWidth: "100%", "--ap-h1": `${apHeadRowH}px` }}>
              <thead>
                <tr className="bg-[#1E3A5F] text-white">
                  <th className="ap-c1 text-left px-3 py-2.5 font-semibold bg-[#1E3A5F]">Descripción</th>
                  <th className="ap-c2 px-2 py-2.5 font-semibold text-center bg-[#1E3A5F]">UM</th>
                  <th className="ap-c3 px-2 py-2.5 font-semibold text-center bg-[#1E3A5F]">Cant.</th>
                  <th className="ap-c4 px-2 py-2.5 font-semibold text-center bg-[#1E3A5F]">Cant. Acum.</th>
                  {localColumns.map((col, ci) => {
                    const closedEntry = getClosedEntry(ci);
                    if (closedEntry) {
                      return (
                        <th key={ci} colSpan={5} className="px-2 py-2.5 font-semibold text-center border-l border-white/20 min-w-[320px] bg-slate-700">
                          <div className="flex flex-col items-center gap-1 py-0.5">
                            <span className="truncate max-w-[200px] text-gray-200" title={col}>{col}</span>
                            <span className="inline-flex items-center gap-1 text-[9px] font-bold bg-slate-800 text-gray-100 px-2 py-0.5 rounded-full whitespace-nowrap" data-testid={`closed-provider-badge-${ci}`}>
                              <BadgeCheck size={9} /> Cerrado · {closedEntry.closed_by_name} · {formatDate(closedEntry.closed_at)}
                            </span>
                            {Object.keys(closedEntry.aprobaciones || {}).length > 0 && (
                              <span className="text-[9px] text-emerald-200" data-testid={`closed-provider-aprobo-${ci}`}>
                                Aprobó: {Object.values(closedEntry.aprobaciones).map(v => `${v.perfil} (${v.by_name})`).join(" · ")}
                              </span>
                            )}
                            {canDelete && (
                              <button onClick={() => handleReopenProvider(ci, col)} disabled={reopeningProviderIdx === ci}
                                className="flex items-center gap-1 text-[9px] font-semibold text-amber-300 hover:text-amber-100 disabled:opacity-50 transition-colors"
                                data-testid={`reopen-provider-btn-${ci}`}>
                                {reopeningProviderIdx === ci ? <Loader2 size={9} className="animate-spin" /> : <RotateCcw size={9} />} Reabrir proveedor
                              </button>
                            )}
                          </div>
                        </th>
                      );
                    }
                    return (
                      <th key={ci} colSpan={5} className="px-2 py-2.5 font-semibold text-center border-l border-white/20 min-w-[320px] bg-[#1E3A5F]">
                        <div className="flex flex-col items-center gap-1 py-0.5">
                          <div className="flex items-center justify-center gap-1.5">
                            <span className="truncate max-w-[150px]" title={col}>{col}</span>
                            {!isActFullyClosed && (
                              <button onClick={() => removeProviderColumn(ci)}
                                className="opacity-50 hover:opacity-100 hover:text-red-300 transition-all flex-shrink-0">
                                <X size={10} />
                              </button>
                            )}
                          </div>
                          {!isActFullyClosed && canEdit && (
                            <button onClick={() => handleCloseProvider(ci)} disabled={closingProviderIdx === ci}
                              title={`Cerrar el análisis solo con "${col}" — genera su PF de inmediato`}
                              className="flex items-center gap-1 text-[9px] font-bold bg-emerald-600 hover:bg-emerald-500 text-white px-2 py-0.5 rounded-full disabled:opacity-50 transition-all whitespace-nowrap"
                              data-testid={`close-provider-btn-${ci}`}>
                              {closingProviderIdx === ci ? <Loader2 size={9} className="animate-spin" /> : <BadgeCheck size={9} />} Cerrar con proveedor
                            </button>
                          )}
                          {aprobacionPorProveedor && (
                            <AprobacionProveedorAP act={actEditor} cfg={rangosAprob} idx={ci}
                              onAprobar={() => aprobarProveedor(ci)} trabajando={aprobandoProv === ci} />
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
                <tr className="bg-[#3D7AAD] text-blue-100 text-[10px]">
                  <th className="ap-c1 bg-[#3D7AAD] px-3 py-1"></th>
                  <th className="ap-c2 bg-[#3D7AAD]"></th>
                  <th className="ap-c3 bg-[#3D7AAD]"></th>
                  <th className="ap-c4 bg-[#3D7AAD]"></th>
                  {localColumns.map((_, ci) => (
                    <th key={`sub-${ci}`} colSpan={5} className="border-l border-white/10 bg-[#3D7AAD]">
                      <div className="grid grid-cols-5 text-center">
                        <span className="py-1 px-2 border-r border-white/10">P.U.</span>
                        <span className="py-1 px-2 border-r border-white/10">Cantidad</span>
                        <span className="py-1 px-2 border-r border-white/10">Subtotal</span>
                        <span className="py-1 px-2 border-r border-white/10">Obs.</span>
                        <span className="py-1 px-1">Sel.</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {localItems.map((item, ii) => {
                  const allocations = item.allocations || [];
                  const acumulada = allocations.reduce((s, a) => s + (Number(a.quantity) || 0), 0);
                  const solicitada = item.quantity || 0;
                  const diff = acumulada - solicitada;
                  // BLOQUE 18 PARTE B — azul = coincide exacto; verde = a favor (de más);
                  // rojo = en contra (falta) — acumulada=0 se trata igual que rojo.
                  const acumColor = acumulada === 0
                    ? "bg-red-50 text-red-700 border-red-200"
                    : diff === 0 ? "bg-blue-50 text-blue-700 border-blue-200"
                    : diff > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-red-50 text-red-700 border-red-200";
                  return (
                    <tr key={ii} className={ii % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="ap-c1 bg-inherit px-3 py-2 font-medium text-gray-800">
                        {item.item_name}
                        <DatosDelPedido item={item} pp={(supplierOrders || []).find(p => p._id === activeAct?.pp_id)} />
                      </td>
                      <td className="ap-c2 bg-inherit px-2 py-2 text-gray-500 text-center">{item.unit}</td>
                      <td className="ap-c3 bg-inherit px-1.5 py-1.5 text-center">
                        <input
                          type="number"
                          value={item.quantity || 1}
                          onChange={e => updateQuantity(ii, e.target.value)}
                          className="w-14 border border-gray-200 rounded-lg px-1.5 py-1.5 text-xs font-bold text-center focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] focus:outline-none"
                          min="1"
                          data-testid={`qty-input-${ii}`}
                        />
                      </td>
                      <td className="ap-c4 bg-inherit px-1.5 py-2 text-center">
                        <span className={`inline-block px-2 py-1 rounded-lg text-[11px] font-bold border whitespace-nowrap ${acumColor}`} data-testid={`acum-badge-${ii}`}>
                          {acumulada} / {solicitada}
                        </span>
                        {/* CEN 2.0 FASE 3 PARTE B — presupuestado/comprado del ítem vinculado */}
                        {item.item_uid && apConsumoMap[item.item_uid] && (
                          <p className={`text-[8px] font-semibold mt-1 leading-tight ${CONSUMO_ESTADO_TXT[apConsumoMap[item.item_uid].estado] || "text-gray-400"}`} data-testid={`ap-item-consumo-${ii}`}>
                            Presup: {apConsumoMap[item.item_uid].presupuestado} · Compr: {apConsumoMap[item.item_uid].comprado} ({apConsumoMap[item.item_uid].pct_comprado}%)
                          </p>
                        )}
                      </td>
                      {localColumns.map((_, ci) => {
                        const price = (item.prices || [])[ci] || { unit_price: 0, obs: "" };
                        const alloc = allocations.find(a => a.provider_idx === ci);
                        const isAllocated = !!alloc;
                        const allocQty = isAllocated ? (Number(alloc.quantity) || 0) : 0;
                        const subtotal = (price.unit_price || 0) * allocQty;
                        const colClosed = isProviderClosed(ci);
                        // CEN 2.0 FASE 3 PARTE C — semáforo de precios vs costo de referencia
                        const sema = item.ref_precio_mat ? priceSemaforo(price.unit_price, item.ref_precio_mat) : null;
                        return (
                          <td key={`cell-${ci}`} colSpan={5} className={`border-l border-gray-100 p-0 ${isAllocated ? "bg-emerald-50" : ""} ${colClosed ? "bg-slate-100/70" : ""}`}>
                            <div className="grid grid-cols-5">
                              <div className="px-1.5 py-1.5">
                                <input
                                  type="number"
                                  value={price.unit_price || ""}
                                  onChange={e => updatePrice(ii, ci, "unit_price", e.target.value)}
                                  placeholder="0"
                                  disabled={colClosed}
                                  title={item.ref_precio_mat ? `Costo de referencia del presupuesto: ${formatPYG(item.ref_precio_mat)}` : undefined}
                                  className={`w-full border rounded-lg px-2 py-1.5 text-right text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none ${colClosed ? "text-gray-500 cursor-not-allowed" : ""} ${sema ? `${sema.cls} font-semibold` : colClosed ? "border-gray-200 bg-gray-100" : isAllocated ? "border-emerald-300 bg-emerald-50/80 font-semibold" : "border-gray-200 bg-white"}`}
                                  data-testid={`price-input-${ii}-${ci}`}
                                />
                                {sema && (
                                  <p className={`text-[9px] font-bold mt-0.5 text-center ${sema.textCls}`} data-testid={`price-diff-${ii}-${ci}`}>
                                    {sema.diffPct > 0 ? "+" : ""}{sema.diffPct.toFixed(1)}% vs ref
                                  </p>
                                )}
                              </div>
                              <div className="px-1.5 py-1.5">
                                <input
                                  type="number"
                                  value={isAllocated ? allocQty : ""}
                                  disabled={!isAllocated || colClosed}
                                  placeholder="—"
                                  onChange={e => updateAllocationQty(ii, ci, e.target.value)}
                                  className={`w-full border rounded-lg px-2 py-1.5 text-right text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none ${colClosed ? "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed" : isAllocated ? "border-emerald-300 bg-white font-semibold" : "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed"}`}
                                  data-testid={`alloc-qty-input-${ii}-${ci}`}
                                />
                              </div>
                              <div className={`px-2 py-2 text-right font-medium text-xs flex items-center justify-end border-r border-gray-100 ${isAllocated ? "text-emerald-700 font-bold" : "text-gray-600"}`}>
                                {subtotal > 0 ? formatPYG(subtotal) : "—"}
                              </div>
                              <div className="px-1.5 py-1.5">
                                <input
                                  type="text"
                                  value={price.obs || ""}
                                  onChange={e => updatePrice(ii, ci, "obs", e.target.value)}
                                  placeholder="Obs."
                                  disabled={colClosed}
                                  className={`w-full border rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none ${colClosed ? "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed" : "border-gray-200 bg-white"}`}
                                  data-testid={`obs-input-${ii}-${ci}`}
                                />
                              </div>
                              <div className="px-1 py-1.5 flex items-center justify-center">
                                <button
                                  onClick={() => !colClosed && selectProvider(ii, ci)}
                                  disabled={colClosed}
                                  title={colClosed ? "Proveedor cerrado — no editable" : (isAllocated ? `Quitar ${localColumns[ci]}` : `Agregar ${localColumns[ci]}`)}
                                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${colClosed ? "bg-gray-200 border-gray-300 cursor-not-allowed" : isAllocated ? "bg-emerald-500 border-emerald-500 shadow-sm" : "bg-white border-gray-300 hover:border-emerald-400"}`}
                                  data-testid={`select-prov-${ii}-${ci}`}>
                                  {isAllocated && <Check size={11} className={colClosed ? "text-gray-400" : "text-white"} />}
                                </button>
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-bold text-xs border-t-2 border-gray-300">
                  <td colSpan={4} className="ap-foot-c1 px-3 py-2.5 text-gray-600 bg-gray-100">TOTAL POR PROVEEDOR</td>
                  {localColumns.map((_, ci) => (
                    <td key={`total-${ci}`} colSpan={5} className="border-l border-gray-200">
                      <div className="grid grid-cols-5">
                        <div></div>
                        <div></div>
                        <div className="px-2 py-2.5 text-right text-[#2E6DA4]">{formatPYG(providerTotals[ci] || 0)}</div>
                        <div></div>
                        <div></div>
                      </div>
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
            </div>
          </div>
        )}

        <StockPropio filas={stockPropioDe(activeAct, supplierOrders, rsis)} />   {/* Fix FD */}

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones / Criterios de selección</label>
          <textarea
            value={localNotes}
            onChange={e => setLocalNotes(e.target.value)}
            rows={2}
            placeholder="Ej: Se prefirió Proveedor 2 por cercanía a la obra, crédito disponible a 30 días y calidad de marca reconocida..."
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none resize-none"
          />
        </div>

        {selectedTotal > 0 && (
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Total Seleccionado (Mejor Oferta)</p>
              <p className="text-2xl font-bold text-emerald-800 mt-0.5 font-mono">{formatPYG(selectedTotal)}</p>
            </div>
            <BadgeCheck size={36} className="text-emerald-400" />
          </div>
        )}

        {/* Modification log */}
        {(activeAct?.modification_log || []).length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="modification-log">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
              <History size={12} className="text-amber-600" /> Historial de Modificaciones
            </p>
            {(activeAct.modification_log || []).slice().reverse().map((log, i) => {
              const added = log.added_names || [];
              const removed = log.removed_names || [];
              const itemChanges = log.item_changes || [];
              // Backwards compat: if no item_changes but flat changes exist, render those
              const flatChanges = log.changes || [];
              const hasNew = added.length > 0 || removed.length > 0 || itemChanges.length > 0;
              return (
                <div key={i} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`log-entry-${i}`}>
                  <div className="flex items-center justify-between bg-gray-50 px-3 py-2 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xs">
                        {log.by ? log.by.charAt(0).toUpperCase() : "U"}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-800">{log.by || "Usuario"}</p>
                        <p className="text-[10px] text-gray-400">{formatDateTime ? formatDateTime(log.at) : formatDate(log.at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      {added.length > 0 && (
                        <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                          <Plus size={10} /> +{added.length}
                        </span>
                      )}
                      {removed.length > 0 && (
                        <span className="bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                          <X size={10} /> -{removed.length}
                        </span>
                      )}
                      {itemChanges.length > 0 && (
                        <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                          <Pencil size={10} /> {itemChanges.length} mod.
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-3 space-y-2.5">
                    {added.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-emerald-700 uppercase mb-1 flex items-center gap-1">
                          <Plus size={10} /> Ítems agregados
                        </p>
                        <ul className="space-y-1">
                          {added.map((nm, ai) => (
                            <li key={ai} className="text-xs text-emerald-800 bg-emerald-50 border-l-2 border-emerald-400 px-2 py-1 rounded">
                              {nm}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {removed.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-red-700 uppercase mb-1 flex items-center gap-1">
                          <X size={10} /> Ítems eliminados
                        </p>
                        <ul className="space-y-1">
                          {removed.map((nm, ri) => (
                            <li key={ri} className="text-xs text-red-800 bg-red-50 border-l-2 border-red-400 px-2 py-1 rounded line-through opacity-80">
                              {nm}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {itemChanges.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-amber-700 uppercase mb-1 flex items-center gap-1">
                          <Pencil size={10} /> Cambios en ítems
                        </p>
                        <ul className="space-y-1.5">
                          {itemChanges.map((ic, ci) => (
                            <li key={ci} className="text-xs bg-amber-50 border-l-2 border-amber-400 px-2 py-1.5 rounded">
                              <p className="font-semibold text-amber-900">{ic.item_name}{ic.original_name && ic.original_name !== ic.item_name ? <span className="text-amber-600 font-normal text-[10px]"> (antes: {ic.original_name})</span> : null}</p>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {(ic.fields || []).map((f, fi) => (
                                  <span key={fi} className="inline-flex items-center gap-1 text-[10px] bg-white border border-amber-200 rounded px-2 py-0.5">
                                    <span className="text-amber-600 font-semibold capitalize">{f.field}:</span>
                                    <span className="line-through text-red-500">{String(f.old_value)}</span>
                                    <ArrowRight size={9} className="text-gray-400" />
                                    <span className="text-emerald-700 font-bold">{String(f.new_value)}</span>
                                  </span>
                                ))}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* Backwards compat: render flat changes only when there are no structured entries */}
                    {!hasNew && flatChanges.length > 0 && (
                      <div className="space-y-0.5">
                        {flatChanges.map((c, ci) => (
                          <p key={ci} className="text-[11px] text-amber-700 font-mono">
                            <span className="font-semibold">{c.item_name}:</span> {c.field} {c.old_value !== undefined ? `${c.old_value} → ` : ""}{c.new_value}
                          </p>
                        ))}
                      </div>
                    )}
                    {/* BLOQUE 22 PARTE B — entradas genéricas de acción (ej. "reopen") sin cambios de ítems */}
                    {!hasNew && flatChanges.length === 0 && log.comment && (
                      <p className="text-xs text-amber-800 flex items-center gap-1.5">
                        <RotateCcw size={11} className="text-amber-500" /> {log.comment}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Fix EJ (2026-09-27): verificar stock y asignar ítems al inventario. Antes se guarda
            lo cargado en pantalla, para no perder precios sin guardar. */}
        <AsignarStockAPModal act={activeAct} items={stockSnap || []} open={!!stockSnap}
          cerrados={closedProviders.map(cp => cp.provider_idx)}
          onClose={() => setStockSnap(null)}
          onGuardarAntes={() => axios.put(`${API}/purchases/budget-acts/${activeAct._id}`,
            { provider_columns: localColumns, analysis_items: localItems, notes: localNotes }, { withCredentials: true })}
          onListo={(act) => { openForEdit(act); onRefresh(); }} />

        {/* Fix GC (2026-10-14): compra desde Caja Chica. Igual que Verificar stock, antes se guarda
            lo cargado en pantalla. */}
        <CompraCajaChicaAPModal act={activeAct} items={ccSnap || []} open={!!ccSnap}
          cerrados={closedProviders.map(cp => cp.provider_idx)}
          onClose={() => setCcSnap(null)}
          onGuardarAntes={() => axios.put(`${API}/purchases/budget-acts/${activeAct._id}`,
            { provider_columns: localColumns, analysis_items: localItems, notes: localNotes }, { withCredentials: true })}
          onListo={(act) => { openForEdit(act); onRefresh(); }} />

        {/* Modal: Modificar Pedido */}
        {showModify && createPortal(
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => !savingModify && setShowModify(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="bg-amber-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center gap-2.5 text-white">
                  <Pencil size={18} />
                  <div>
                    <p className="font-bold text-base">Modificar Ítems del Pedido</p>
                    <p className="text-amber-200 text-xs">{activeAct?.number} — Agregar o modificar ítems</p>
                  </div>
                </div>
                <button onClick={() => !savingModify && setShowModify(false)} className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all">
                  <X size={18} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100 text-gray-600 font-semibold">
                        <th className="text-left px-3 py-2.5">#</th>
                        <th className="text-left px-3 py-2.5">Descripción</th>
                        <th className="px-2 py-2.5 text-center">UM</th>
                        <th className="px-2 py-2.5 text-center">Cant.</th>
                        <th className="px-2 py-2.5 text-center w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {modifyItems.map((item, idx) => (
                        <tr key={idx} className={item._isNew ? "bg-amber-50/40" : item.sub_de_item_id !== undefined ? "bg-sky-50/50" : "bg-white"} data-testid={`modify-row-${idx}`}>
                          <td className="px-3 py-1.5 text-gray-400 font-mono text-[10px]">
                            {item._isNew ? <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold text-[9px]">NUEVO</span> : numerosModify[idx]}
                          </td>
                          <td className="px-1.5 py-1.5">
                            <input value={item.item_name} onChange={e => updateModifyItem(idx, "item_name", e.target.value)}
                              placeholder="Descripción del ítem"
                              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-amber-500/20 focus:outline-none" />
                            {item.sub_de_item_id !== undefined && (
                              <p className="text-[10px] text-sky-700 mt-0.5 pl-1" data-testid={`modify-sub-leyenda-${idx}`}>Sub-ítem de «{item.sub_de_nombre}» · misma imputación</p>
                            )}
                          </td>
                          <td className="px-1.5 py-1.5">
                            <input value={item.unit} onChange={e => updateModifyItem(idx, "unit", e.target.value)}
                              placeholder="un"
                              className="w-14 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-center focus:ring-2 focus:ring-amber-500/20 focus:outline-none" />
                          </td>
                          <td className="px-1.5 py-1.5">
                            <input type="number" value={item.quantity} onChange={e => updateModifyItem(idx, "quantity", e.target.value)}
                              min="1"
                              className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-center focus:ring-2 focus:ring-amber-500/20 focus:outline-none" />
                          </td>
                          <td className="px-1.5 py-1.5 text-center whitespace-nowrap">
                            {item.sub_de_item_id === undefined && !item._isNew && (
                              <button onClick={() => addSubItem(idx)} title="Agregar un sub-ítem con la misma imputación"
                                className="px-1.5 py-0.5 mr-1 text-[10px] font-bold text-sky-700 border border-sky-300 rounded hover:bg-sky-50"
                                data-testid={`add-sub-item-${idx}`}>
                                + Sub
                              </button>
                            )}
                            <button onClick={() => removeModifyItem(idx)} className="p-1 text-red-400 hover:text-red-600 rounded transition-colors">
                              <X size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {modifyItems.some(i => i.item_uid || (i.lineas_pm || []).length) ? (
                  <p className="text-[11px] text-sky-700" data-testid="modify-sub-hint">Para agregar un material, usá «+ Sub» en la línea a la que corresponde: queda como sub-ítem con su misma imputación.</p>
                ) : (
                <button onClick={addModifyItem}
                  className="flex items-center gap-1.5 text-xs text-amber-600 font-semibold hover:text-amber-700 border border-dashed border-amber-300 px-3 py-2 rounded-lg hover:bg-amber-50 transition-all">
                  <Plus size={12} /> Agregar nuevo ítem
                </button>
                )}
                <div className="flex gap-3 pt-2 border-t border-gray-100">
                  <button onClick={() => setShowModify(false)}
                    className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">
                    Cancelar
                  </button>
                  <button onClick={handleSaveModify} disabled={savingModify}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                    data-testid="save-modify-btn">
                    {savingModify ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                    {savingModify ? "Guardando..." : "Guardar modificaciones"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        , document.body)}

        {/* ── AI ANALYSIS MODAL ── */}
        {aiModal.open && createPortal(
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => !aiModal.loading && setAiModal(prev => ({ ...prev, open: false }))}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="bg-gradient-to-r from-violet-700 to-violet-500 px-6 py-4 rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center gap-2.5 text-white">
                  <Brain size={20} />
                  <div>
                    <p className="font-bold text-base">Analizar presupuesto con IA</p>
                    <p className="text-violet-200 text-xs">{activeAct?.number} · Gemini Flash</p>
                  </div>
                </div>
                <button onClick={() => !aiModal.loading && setAiModal(prev => ({ ...prev, open: false }))}
                  className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all">
                  <X size={18} />
                </button>
              </div>

              <div className="p-6 space-y-5">
                {/* Step 1: Provider & File */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Proveedor a rellenar *
                    </label>
                    <select
                      value={aiModal.providerIdx}
                      onChange={e => setAiModal(prev => ({ ...prev, providerIdx: Number(e.target.value) }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 focus:outline-none"
                      data-testid="ai-provider-select">
                      {localColumns.map((col, ci) => (
                        <option key={ci} value={ci}>{`${ci + 1}. ${col}`}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">Los precios extraídos se aplicarán a la columna seleccionada. Si la IA detecta que la cotización pertenece a otro proveedor de la lista, la columna destino se ajusta automáticamente.</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Documento del proveedor *
                    </label>
                    <input
                      ref={aiFileInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={handleAIFileChange}
                      className="hidden"
                      data-testid="ai-file-input"
                    />
                    {!aiModal.file ? (
                      <button
                        onClick={() => aiFileInputRef.current?.click()}
                        className="w-full border-2 border-dashed border-violet-300 rounded-xl p-8 flex flex-col items-center gap-2 hover:border-violet-500 hover:bg-violet-50 transition-all group">
                        <FileUp size={28} className="text-violet-400 group-hover:text-violet-600 transition-colors" />
                        <p className="text-sm font-medium text-gray-600 group-hover:text-violet-700">Clic para cargar documento</p>
                        <p className="text-xs text-gray-400">PDF, PNG, JPG, WEBP — máx. 20 MB</p>
                      </button>
                    ) : (
                      <div className="flex items-center gap-3 border border-violet-200 bg-violet-50 rounded-xl px-4 py-3">
                        <FileText size={20} className="text-violet-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{aiModal.file.name}</p>
                          <p className="text-xs text-gray-400">{(aiModal.file.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <button onClick={() => { setAiModal(prev => ({ ...prev, file: null, results: null })); if (aiFileInputRef.current) aiFileInputRef.current.value = ""; }}
                          className="text-gray-400 hover:text-red-500 p-1 rounded-lg transition-colors">
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleAIAnalyze}
                    disabled={aiModal.loading || !aiModal.file}
                    className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-3 rounded-xl font-semibold text-sm transition-all shadow-sm"
                    data-testid="ai-analyze-btn">
                    {aiModal.loading ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        Analizando documento con IA...
                      </>
                    ) : (
                      <>
                        <Sparkles size={15} />
                        Analizar con IA
                      </>
                    )}
                  </button>
                </div>

                {/* Step 2: Results */}
                {aiModal.results && (
                  <div className="space-y-4 border-t border-gray-100 pt-4">
                    {/* Provider detected */}
                    {aiModal.results.proveedor_sugerido && (
                      <div className="flex items-center gap-2 bg-violet-50 rounded-xl border border-violet-200 px-4 py-2.5">
                        <Building2 size={14} className="text-violet-500" />
                        <span className="text-xs text-gray-600">Proveedor detectado:</span>
                        <span className="text-sm font-semibold text-violet-700">{aiModal.results.proveedor_sugerido}</span>
                        {aiModal.results.moneda_original !== "PYG" && (
                          <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">
                            Precios convertidos desde {aiModal.results.moneda_original}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Obs general */}
                    {aiModal.results.obs_general && (
                      <p className="text-xs text-gray-500 italic">{aiModal.results.obs_general}</p>
                    )}

                    {/* Prices table */}
                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                      <table className="text-xs w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left px-3 py-2.5 font-semibold text-gray-600">Ítem (IA)</th>
                            <th className="text-left px-3 py-2.5 font-semibold text-gray-600">Se aplicará a</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-gray-600">P.U. Extraído (Gs)</th>
                            <th className="text-center px-3 py-2.5 font-semibold text-gray-600">Confianza</th>
                            <th className="text-left px-3 py-2.5 font-semibold text-gray-600">Nota IA</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {aiModal.results.precios_extraidos?.map((p, i) => {
                            const confColor = p.confianza === "alta" ? "bg-emerald-100 text-emerald-700" : p.confianza === "media" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600";
                            const targetIdx = _resolveAiTargetIdx(p, localItems);
                            const targetItem = targetIdx >= 0 ? localItems[targetIdx] : null;
                            const wasCorrected = targetIdx >= 0 && targetIdx !== p.item_index;
                            return (
                              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"} data-testid={`ai-price-row-${i}`}>
                                <td className="px-3 py-2 font-medium text-gray-800">{p.item_name}</td>
                                <td className="px-3 py-2">
                                  {targetItem ? (
                                    <span className={`font-medium ${wasCorrected ? "text-amber-700" : "text-gray-700"}`} data-testid={`ai-price-target-${i}`}>
                                      {targetItem.item_name}
                                      {wasCorrected && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold uppercase" data-testid={`ai-price-corrected-${i}`}>corregido</span>}
                                    </span>
                                  ) : <span className="text-gray-400">— sin destino —</span>}
                                </td>
                                <td className="px-3 py-2 text-right font-mono font-bold text-gray-900">
                                  {p.precio_unitario !== null && p.precio_unitario !== undefined
                                    ? formatPYG(p.precio_unitario)
                                    : <span className="text-gray-400 font-normal">No encontrado</span>}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${confColor}`}>
                                    {p.confianza || "—"}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-500 italic">{p.nota || "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Stats */}
                    {(() => {
                      const found = aiModal.results.precios_extraidos?.filter(p => p.precio_unitario !== null && p.precio_unitario !== undefined).length || 0;
                      const total = aiModal.results.precios_extraidos?.length || 0;
                      return (
                        <p className="text-xs text-gray-500 text-center">
                          {found}/{total} ítems con precio extraído
                        </p>
                      );
                    })()}

                    {/* Apply button */}
                    <button
                      onClick={handleApplyAIPrices}
                      className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-xl font-semibold text-sm transition-all shadow-sm"
                      data-testid="ai-apply-prices-btn">
                      <CheckCheck size={15} />
                      Aplicar precios a "{localColumns[aiModal.providerIdx]}"
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        , document.body)}
        {/* Fix GM: opciones del PDF (ítems y proveedores) también dentro del análisis */}
        <SelectAPPdfOptionsModal
          open={!!pdfSelectAct}
          onClose={() => setPdfSelectAct(null)}
          act={pdfSelectAct}
          onConfirm={handleConfirmPdfSelection}
        />
      </div>
    );
  }

  // ---- LIST VIEW ----
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-500">{filteredActs.length} de {budgetActs.length} actas de presupuesto</p>
        <div className="flex items-center gap-2 flex-wrap">
          <FiltroClienteObraPresupuesto
            listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets}
            filterCliente={filterCliente} setFilterCliente={setFilterCliente}
            filterObra={filterObra} setFilterObra={setFilterObra}
            filterPresupuesto={filterPresupuesto} setFilterPresupuesto={setFilterPresupuesto}
            testIdPrefix="analisis"
          />
          <BusquedaEstadoUrgenciaFilters
            search={search} setSearch={setSearch}
            searchPlaceholder="Buscar por N°, PP, obra o proveedor..."
            filterStatus={filterStatus} setFilterStatus={setFilterStatus}
            statusOptions={[
              { value: "todos", label: `Todos (${baseFilteredActs.length})` },
              { value: "abierta", label: `Abierta (${apStatusCounts.abierta})` },
              { value: "parcial", label: `Cierre Parcial (${apStatusCounts.parcial})` },
              { value: "cerrada", label: `Cerrada (${apStatusCounts.cerrada})` },
            ]}
            filterUrgency={filterUrgency} setFilterUrgency={setFilterUrgency}
            testIdPrefix="analisis"
          />
          {(filterCliente || filterObra || filterPresupuesto || search || filterUrgency || filterStatus !== "todos") && (
            <button onClick={() => { setFilterCliente(""); setFilterObra(""); setFilterPresupuesto(""); setSearch(""); setFilterUrgency(""); setFilterStatus("todos"); }}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-xl px-2 py-2 hover:bg-gray-50 transition-all" title="Limpiar filtros"
              data-testid="analisis-clear-filter">
              <X size={12} /> Limpiar
            </button>
          )}
        </div>
      </div>

      {/* PENDIENTES: PPs sin AP — lista inline */}
      {canEdit && openPPs.length > 0 && (
        <div className="bg-amber-50 rounded-xl border border-amber-200 overflow-hidden">
          <div className="px-4 py-2.5 bg-amber-100/70 border-b border-amber-200 flex items-center gap-2">
            <Clock size={13} className="text-amber-600" />
            <p className="text-xs font-bold text-amber-800">Pedidos de Ppto. (PP) pendientes de análisis — {openPPs.length}</p>
          </div>
          <div className="divide-y divide-amber-100/60">
            {openPPs.map(pp => (
              <div key={pp._id} className="flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-amber-50/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-bold text-gray-900 font-mono">{pp.number}</span>
                  <span className="text-xs text-gray-500 ml-2">{pp.project_name} · {(pp.items || []).length} ítems · {pp.provider_name || pp.provider || "—"}</span>
                </div>
                <button
                  onClick={() => handleCreateAP(pp._id)}
                  disabled={creating}
                  className="flex items-center gap-1 text-xs bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-semibold flex-shrink-0 transition-all"
                  data-testid={`generar-ap-inline-${pp._id}`}>
                  {creating ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                  Generar AP
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-purple-50/70 rounded-xl border border-purple-100 p-4 text-xs text-purple-800">
        <p className="font-semibold mb-1">Análisis de Presupuesto (AP)</p>
        <p>Compara precios de múltiples proveedores ítem por ítem. Seleccione la mejor oferta por criterios: precio, cercanía, crédito disponible, calidad de marca. Al cerrar se genera el Acta formal (AP).</p>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20" />)}</div>
      ) : filteredActs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200/60 p-12 text-center">
          <BarChart2 size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No hay análisis de presupuesto</p>
          <p className="text-xs text-gray-400 mt-1">
            {openPPs.length > 0 ? "Cree un análisis desde un PP existente" : "Primero genere un Pedido de Presupuesto (PP)"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredActs.map(act => {
            const closedCount = (act.closed_providers || []).length;
            const totalProviderColumns = (act.provider_columns || []).length;
            // BLOQUE 45 PARTE A — el denominador de la píldora "X/Y proveedores
            // cerrados" cuenta solo proveedores CON adjudicaciones (mismo criterio
            // que el backend para disparar el cierre total, Bloque 39A) — nunca
            // todas las columnas, para no confundir con un "7/8" cuando en realidad
            // ya están cerrados los 7 que tenían algo que adjudicar.
            const providersWithAlloc = countProvidersWithAllocations(act.provider_columns, act.analysis_items);
            const hasPartialClose = closedCount > 0 && act.status === "abierta";
            return (
            <div key={act._id} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm hover:shadow-md transition-all" data-testid="ap-card">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-gray-900 text-sm font-mono">{act.number}</p>
                    <StatusBadge status={act.status === "abierta" ? "analisis_presupuesto" : "presupuesto_aprobado"} />
                    {act.status === "cerrada" && <BadgeCheck size={15} className="text-emerald-500" />}
                    {hasPartialClose && (
                      <span className="text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full" data-testid={`ap-card-partial-badge-${act._id}`}>
                        {closedCount}/{providersWithAlloc} proveedores cerrados
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 font-medium mt-1">{act.project_name} · PP: {act.pp_number}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-400">{totalProviderColumns} proveedores · {(act.analysis_items || []).length} ítems</span>
                    {act.selected_total > 0 && (
                      <span className="text-xs font-bold text-emerald-600">{formatPYG(act.selected_total)}</span>
                    )}
                  </div>
                  {hasPartialClose && (
                    <p className="text-[11px] text-purple-600 mt-0.5">
                      Total cerrado parcial: <span className="font-bold">{formatPYG((act.closed_providers || []).reduce((s, cp) => s + (cp.total_cerrado || 0), 0))}</span>
                    </p>
                  )}
                  {act.status === "cerrada" && act.closed_at && (
                    <p className="text-xs text-gray-400 mt-0.5">Cerrado por {act.closed_by_name} · {formatDate(act.closed_at)}</p>
                  )}
                  {/* Fix EQ (2026-09-30): todo el pedido salió del stock interno */}
                  {act.cubierto_por_stock && (
                    <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full" data-testid={`ap-cubierto-stock-${act._id}`}>
                      <Boxes size={10} /> {act.cubierto_por_caja_chica ? "Cubierto por stock y Caja Chica" : "Cubierto por stock interno"}
                    </span>
                  )}
                  {act.status !== "cerrada" && <ChipAprobacionAP act={act} cfg={rangosAprob} />}
                  {act.status === "cerrada" && !act.pf_generated && !act.cubierto_por_stock && (
                    <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                      <FileCheck size={10} /> PF pendiente de generar
                    </span>
                  )}
                  {act.pf_generated && (
                    <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                      <BadgeCheck size={10} /> {act.pf_count || 1} PF generado(s)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* BLOQUE 42 — el ícono "Editar análisis" se muestra si y solo si el acta NO
                      está cerrada. No depende de closed_providers ni de pf_generated: un acta
                      abierta con cierres parciales SÍ lo muestra (tiene columnas editables),
                      un acta cerrada NO (solo lectura + reabrir). */}
                  {canEdit && act.status !== "cerrada" && (
                    <button onClick={() => openForEdit(act)} title="Editar análisis"
                      className="p-1.5 rounded-lg hover:bg-purple-50 text-purple-600 transition-colors"
                      data-testid="edit-ap-btn">
                      <BarChart2 size={15} />
                    </button>
                  )}
                  <button onClick={() => setViewAct(act)} title="Ver acta"
                    className="p-1.5 rounded-lg hover:bg-blue-50 text-[#2E6DA4] transition-colors"
                    data-testid="ver-ap-btn">
                    <Eye size={15} />
                  </button>
                  <button onClick={() => openPdfSelect(act)} title="Descargar PDF"
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    data-testid="pdf-ap-btn">
                    <Download size={15} />
                  </button>
                  {act.status === "cerrada" && canDelete && !act.cubierto_por_stock && (
                    <button
                      onClick={() => closedCount > 0 ? openForEdit(act) : handleReopenAP(act)}
                      disabled={reopeningId === act._id}
                      title={closedCount > 0
                        ? "Reabrir análisis (completo o un proveedor cerrado puntual)"
                        : "Reabrir análisis (volver a seleccionar proveedores)"}
                      className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-500 hover:text-amber-600 transition-colors disabled:opacity-50"
                      data-testid={`reopen-ap-btn-${act._id}`}>
                      {reopeningId === act._id ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
                    </button>
                  )}
                  {canDelete && act.status === "abierta" && (
                    <button onClick={() => setDeleteTarget(act)} title={closedCount > 0 ? "No se puede eliminar: tiene proveedor(es) con cierre parcial" : "Eliminar AP"}
                      disabled={closedCount > 0}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                      data-testid={`delete-ap-btn-${act._id}`}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Modal VER AP */}
      {viewAct && (
        <Modal open={!!viewAct} onClose={() => setViewAct(null)} title={`AP: ${viewAct.number}`} extraWide>
          <div className="space-y-4">
            <div className="bg-purple-700 rounded-xl p-5 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs opacity-70 uppercase tracking-wider">CEN INGENIERIA E.A.S.</p>
                  <p className="text-xs opacity-60 mt-0.5">Acta de Presupuesto Comparativa</p>
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold text-xl">{viewAct.number}</p>
                  <StatusBadge status={viewAct.status === "cerrada" ? "presupuesto_aprobado" : "analisis_presupuesto"} />
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-3 text-xs opacity-80">
                <span>Proyecto: {viewAct.project_name}</span>
                <span>PP: {viewAct.pp_number}</span>
                <span>Fecha Objetivo: {viewAct.target_date ? formatDate(viewAct.target_date) : "—"}</span>
                {viewAct.selected_total > 0 && (
                  <span className="font-bold text-white opacity-100">Total Seleccionado: {formatPYG(viewAct.selected_total)}</span>
                )}
              </div>
            </div>

            {/* Fix EX (2026-10-05): aprobación por rango de monto */}
            {viewAct.status !== "cerrada" && (
              <AprobacionAP act={viewAct} cfg={rangosAprob} onAprobar={() => aprobarAP(true)} trabajando={aprobandoAP} />
            )}

            {/* Fix O (2026-08-31): historial del pedido de origen en cada paso del flujo. */}
            <HistorialPedidoCard pmId={viewAct.pm_origen_id} />

            {(viewAct.provider_columns || []).length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="text-left px-3 py-2.5">Ítem</th>
                      <th className="px-2 py-2.5 text-center">UM</th>
                      <th className="px-2 py-2.5 text-center">Cant.</th>
                      <th className="px-2 py-2.5 text-center">Cant. Acum.</th>
                      {(viewAct.provider_columns || []).map((col, ci) => {
                        const isClosed = (viewAct.closed_providers || []).some(cp => cp.provider_idx === ci);
                        return (
                          <th key={ci} colSpan={3} className={`px-2 py-2.5 text-center border-l border-white/20 ${isClosed ? "bg-slate-700" : ""}`}>
                            {col}{isClosed && <span className="ml-1 text-[9px] font-bold text-amber-300">(CERRADO)</span>}
                          </th>
                        );
                      })}
                      <th className="px-2 py-2.5 text-center border-l border-white/20">Proveedor(es)</th>
                    </tr>
                    <tr className="bg-gray-800 text-gray-300 text-[10px]">
                      <th colSpan={4}></th>
                      {(viewAct.provider_columns || []).map((_, ci) => (
                        <th key={ci} colSpan={3} className="border-l border-white/10">
                          <div className="grid grid-cols-3 text-center">
                            <span className="py-1 border-r border-white/10">P.U.</span>
                            <span className="py-1 border-r border-white/10">Cant.</span>
                            <span className="py-1">Subtotal</span>
                          </div>
                        </th>
                      ))}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewAct.analysis_items || []).map((item, ii) => {
                      const allocations = getItemAllocations(item);
                      const acumulada = allocations.reduce((s, a) => s + (Number(a.quantity) || 0), 0);
                      const solicitada = item.quantity || 0;
                      const diff = acumulada - solicitada;
                      const acumColor = acumulada === 0
                        ? "bg-red-50 text-red-700 border-red-200"
                        : diff === 0 ? "bg-blue-50 text-blue-700 border-blue-200"
                        : diff > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-red-50 text-red-700 border-red-200";
                      return (
                        <tr key={ii} className={ii % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                          <td className="px-3 py-2 font-medium text-gray-800">
                            {item.item_name}
                            <DatosDelPedido item={item} pp={(supplierOrders || []).find(p => p._id === viewAct?.pp_id)} />
                          </td>
                          <td className="px-2 py-2 text-gray-500 text-center">{item.unit}</td>
                          <td className="px-2 py-2 font-bold text-center">{item.quantity}</td>
                          <td className="px-2 py-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${acumColor}`}>
                              {acumulada} / {solicitada}
                            </span>
                          </td>
                          {(viewAct.provider_columns || []).map((_, ci) => {
                            const price = (item.prices || [])[ci] || {};
                            const alloc = allocations.find(a => a.provider_idx === ci);
                            const isWinner = !!alloc;
                            const allocQty = isWinner ? (Number(alloc.quantity) || 0) : 0;
                            const st = (price.unit_price || 0) * allocQty;
                            return (
                              <td key={ci} colSpan={3} className={`border-l border-gray-100 p-0 ${isWinner ? "bg-emerald-50" : ""}`}>
                                <div className="grid grid-cols-3">
                                  <div className={`px-2 py-2 text-right ${isWinner ? "text-emerald-700 font-bold" : "text-gray-700"}`}>
                                    {price.unit_price > 0 ? formatPYG(price.unit_price) : "—"}
                                  </div>
                                  <div className={`px-2 py-2 text-right ${isWinner ? "text-emerald-700 font-bold" : "text-gray-600"}`}>
                                    {isWinner ? allocQty : "—"}
                                  </div>
                                  <div className={`px-2 py-2 text-right ${isWinner ? "text-emerald-700 font-bold" : "text-gray-600"}`}>
                                    {st > 0 ? formatPYG(st) : "—"}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-2 py-2 text-center border-l border-gray-100">
                            {allocations.length > 0 ? (
                              <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-bold border border-emerald-200 whitespace-nowrap">
                                {allocations.map(a => `${(viewAct.provider_columns || [])[a.provider_idx] || "—"} (${a.quantity})`).join(" + ")}
                              </span>
                            ) : <span className="text-gray-300 text-[10px]">Sin sel.</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">Sin datos de análisis cargados aún.</p>
            )}

            <StockPropio filas={stockPropioDe(viewAct, supplierOrders, rsis)} />   {/* Fix FD */}

            {viewAct.notes && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wide">Observaciones / Criterios</p>
                <p className="text-sm text-blue-800 mt-1">{viewAct.notes}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <button onClick={() => openPdfSelect(viewAct)}
                className="flex items-center gap-2 bg-purple-700 hover:bg-purple-800 text-white px-4 py-2.5 rounded-xl text-xs font-semibold transition-all"
                data-testid="pdf-ap-modal-btn">
                <Download size={14} /> Descargar PDF
              </button>
              <button onClick={() => setViewAct(null)}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-xs font-medium">
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal BLOQUE 25 PARTE A — seleccionar ítems (filas) + proveedores (columnas) para el PDF del AP */}
      <SelectAPPdfOptionsModal
        open={!!pdfSelectAct}
        onClose={() => setPdfSelectAct(null)}
        act={pdfSelectAct}
        onConfirm={handleConfirmPdfSelection}
      />

      {/* Modal confirmar eliminación de AP (BLOQUE 6 PARTE C.2) */}
      <Modal open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} title="Eliminar Análisis de Presupuesto">
        {deleteTarget && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                ¿Confirmás eliminar el AP <span className="font-mono font-bold">{deleteTarget.number}</span>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
              <button type="button" onClick={handleDeleteAP} disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="confirm-delete-ap-btn">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ==================== PRESUPUESTOS FINALES (PF) TAB ====================

